import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Checkbox,
  IconButton,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Badge,
  Textarea,
  SimpleGrid,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  useToast,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Switch,
  FormHelperText,
  Divider,
  Flex,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Avatar,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
     InputGroup,
   InputLeftElement,
   InputRightElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorMode,
  Portal,
  List,
  ListItem
} from '@chakra-ui/react';
import { 
  AddIcon, 
  DeleteIcon, 
  EditIcon, 
  CheckIcon, 
  CloseIcon, 
  ViewIcon, 
  CopyIcon,
  SearchIcon,
  SettingsIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  InfoIcon,
  DownloadIcon,
  BellIcon,
  HamburgerIcon,
  SunIcon,
  MoonIcon,
  TriangleDownIcon,
  StarIcon
} from '@chakra-ui/icons';
import api from '../Api/api';
import appConfig from '../Config/appConfig';
import * as FiIcons from 'react-icons/fi';

// Searchable Icon Dropdown Component
const IconSelector = ({ value, onChange, placeholder = "Search icons..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Get all available icons dynamically from FiIcons
  const availableIcons = Object.keys(FiIcons)
    .filter(key => key.startsWith('Fi'))
    .map(key => ({
      name: key.replace('Fi', ''),
      component: key,
      category: 'Feather Icons'
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Filter icons based on search query
  const filteredIcons = availableIcons.filter(icon => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      icon.name.toLowerCase().includes(query) ||
      icon.category.toLowerCase().includes(query)
    );
  });

  // Get selected icon info
  const selectedIcon = availableIcons.find(icon => icon.name === value);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setSelectedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredIcons.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : filteredIcons.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && filteredIcons[selectedIndex]) {
          onChange(filteredIcons[selectedIndex].name);
          setIsOpen(false);
          setSearchQuery('');
          setSelectedIndex(-1);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery('');
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const selectedElement = listRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [selectedIndex]);

  const handleIconSelect = (iconName) => {
    onChange(iconName);
    setIsOpen(false);
    setSearchQuery('');
    setSelectedIndex(-1);
  };

  // Clear search query when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedIndex(-1);
    }
  }, [isOpen]);

  const clearSelection = () => {
    onChange('');
    setSearchQuery('');
    setSelectedIndex(-1);
  };

  return (
    <Box ref={dropdownRef} position="relative" width="100%">
      {/* Input Field */}
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input
          ref={inputRef}
          value={isOpen ? searchQuery : (selectedIcon ? selectedIcon.name : '')}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (isOpen) {
              setSelectedIndex(0);
            }
          }}
          onFocus={() => {
            setIsOpen(true);
            setSelectedIndex(0);
            setSearchQuery(''); // Clear any previous search query
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          size="sm"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.300"
          pr={value ? "60px" : "50px"}
        />
        <InputRightElement width="auto" pr={2}>
          <HStack spacing={1}>
            {value && (
              <IconButton
                icon={<CloseIcon />}
                size="sm"
                variant="ghost"
                onClick={clearSelection}
                aria-label="Clear selection"
                color="gray.400"
                _hover={{ color: "red.400" }}
              />
            )}
            <IconButton
              icon={<ChevronDownIcon />}
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsOpen(!isOpen);
                if (!isOpen) {
                  setSelectedIndex(0);
                  inputRef.current?.focus();
                }
              }}
              aria-label="Toggle dropdown"
              color="gray.400"
              transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
              transition="transform 0.2s"
            />
          </HStack>
        </InputRightElement>
      </InputGroup>

      {/* Dropdown Menu */}
      {isOpen && (
        <Portal>
          <Box
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            zIndex={1400}
            onClick={() => {
              setIsOpen(false);
              setSearchQuery('');
              setSelectedIndex(-1);
            }}
          >
            <Box
              position="absolute"
              top={dropdownRef.current ? dropdownRef.current.getBoundingClientRect().bottom + 8 : 0}
              left={dropdownRef.current ? dropdownRef.current.getBoundingClientRect().left : 0}
              width={dropdownRef.current ? dropdownRef.current.offsetWidth : "100%"}
              maxH="400px"
              overflowY="auto"
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              boxShadow="lg"
              zIndex={1401}
              onClick={(e) => e.stopPropagation()}
            >
              <List spacing={0} ref={listRef}>
                {filteredIcons.length === 0 ? (
                  <ListItem p={4} textAlign="center" color="gray.500">
                    No icons found matching "{searchQuery}"
                  </ListItem>
                ) : (
                  filteredIcons.map((icon, index) => (
                    <ListItem
                      key={icon.name}
                      p={3}
                      cursor="pointer"
                      bg={selectedIndex === index ? "blue.50" : "transparent"}
                      borderBottom="1px solid"
                      borderColor="gray.100"
                      _hover={{ bg: "blue.50" }}
                      _last={{ borderBottom: "none" }}
                      onMouseDown={() => handleIconSelect(icon.name)}
                    >
                      <HStack justify="space-between" w="full" align="center">
                        <HStack spacing={3}>
                          <Box color="blue.600">
                            {FiIcons[icon.component] && React.createElement(FiIcons[icon.component], { size: 20 })}
                          </Box>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium" color="gray.800">
                              {icon.name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {icon.category}
                            </Text>
                          </VStack>
                        </HStack>
                        <Badge colorScheme="blue" size="sm" borderRadius="full">
                          {icon.category}
                        </Badge>
                      </HStack>
                    </ListItem>
                  ))
                )}
              </List>
            </Box>
          </Box>
        </Portal>
      )}
    </Box>
  );
};

// ── Styled Category Dropdown ── //
// Replaces native <Select> for category picking in task-ref forms.
const CategorySelectDropdown = ({ categories, value, onChange, placeholder = "Select a category" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const selectedCat = (value !== '' && value !== undefined && value !== null) ? categories?.[value] : null;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Box ref={ref} position="relative">
      {/* Trigger */}
      <HStack
        px={3} py={2}
        border="1px solid"
        borderColor={isOpen ? "purple.400" : "gray.300"}
        borderRadius="lg"
        bg="white"
        cursor="pointer"
        justify="space-between"
        boxShadow={isOpen ? "0 0 0 2px rgba(139,92,246,0.2)" : "none"}
        _hover={{ borderColor: "purple.300" }}
        transition="all 0.15s"
        minH="32px"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedCat ? (
          <HStack spacing={2} flex={1} minW={0}>
            {selectedCat.icon && FiIcons[`Fi${selectedCat.icon}`] && (
              <Box color="purple.500" flexShrink={0}>
                {React.createElement(FiIcons[`Fi${selectedCat.icon}`], { size: 14 })}
              </Box>
            )}
            <Text fontSize="sm" color="gray.700" fontWeight="medium" noOfLines={1}>{selectedCat.name}</Text>
            <Badge colorScheme="purple" variant="subtle" fontSize="9px" borderRadius="full" ml="auto" flexShrink={0}>
              {selectedCat.options?.length || 0} tasks
            </Badge>
          </HStack>
        ) : (
          <Text fontSize="sm" color="gray.400">{placeholder}</Text>
        )}
        <ChevronDownIcon
          color="gray.400" ml={2} flexShrink={0}
          transform={isOpen ? "rotate(180deg)" : "none"}
          transition="transform 0.15s"
        />
      </HStack>

      {/* Dropdown panel */}
      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left="0" right="0"
          bg="white"
          border="1px solid" borderColor="rgba(139,92,246,0.2)"
          borderRadius="xl"
          boxShadow="0 12px 40px rgba(139,92,246,0.15), 0 4px 12px rgba(0,0,0,0.08)"
          zIndex={9999}
          overflow="hidden"
          maxH="280px"
          overflowY="auto"
        >
          {(!categories || categories.length === 0) ? (
            <Box p={4} textAlign="center">
              <Text fontSize="sm" color="gray.400">No categories available</Text>
            </Box>
          ) : categories.map((category, index) => {
            const CatIcon = category.icon ? FiIcons[`Fi${category.icon}`] : null;
            const isSelected = value === index;
            return (
              <Box
                key={index}
                px={4} py={3}
                cursor="pointer"
                bg={isSelected ? "rgba(139,92,246,0.07)" : "white"}
                borderBottom="1px solid" borderColor="gray.50"
                _hover={{ bg: "rgba(139,92,246,0.05)" }}
                _last={{ borderBottom: "none" }}
                onMouseDown={() => { onChange(index); setIsOpen(false); }}
                display="flex" alignItems="center"
              >
                <Box
                  p={2} mr={3} borderRadius="lg" flexShrink={0}
                  bg={isSelected ? "purple.100" : "gray.50"}
                  color={isSelected ? "purple.600" : "gray.400"}
                >
                  {CatIcon ? React.createElement(CatIcon, { size: 16 }) : <Box w={4} h={4} />}
                </Box>
                <Box flex={1} minW={0}>
                  <Text fontSize="sm" fontWeight={isSelected ? "600" : "500"} color={isSelected ? "purple.700" : "gray.700"}>
                    {category.name}
                  </Text>
                  {category.description && (
                    <Text fontSize="xs" color="gray.400" noOfLines={1}>{category.description}</Text>
                  )}
                </Box>
                <HStack spacing={2} ml={3} flexShrink={0}>
                  <Badge colorScheme="gray" variant="subtle" fontSize="9px" borderRadius="full">
                    {category.options?.length || 0}
                  </Badge>
                  {isSelected && <CheckIcon color="purple.500" boxSize={3} />}
                </HStack>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// Condition Item Component (recursive for nested conditions)
const ConditionItem = ({ 
  condition, 
  index, 
  availableFacts, 
  onUpdate, 
  onRemove, 
  onAddNested, 
  onAddCondition,
  parentPath = [],
  level = 0,
  parentType = 'all'
}) => {
  const isNestedGroup = condition.all || condition.any;
  const nestedType = condition.all ? 'all' : condition.any ? 'any' : null;
  const nestedConditions = condition.all || condition.any || [];

  if (isNestedGroup) {
    // Render nested group
    return (
      <Card 
        bg={level % 2 === 0 ? "purple.50" : "blue.50"} 
        borderRadius="lg" 
        p={4} 
        border="2px solid" 
        borderColor={nestedType === 'all' ? "purple.300" : "blue.300"}
        ml={level * 4}
        position="relative"
      >
        <VStack spacing={3} align="stretch">
          {/* Nested Group Header */}
          <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
            <HStack spacing={2}>
              <Badge 
                colorScheme={nestedType === 'all' ? "purple" : "blue"} 
                borderRadius="full" 
                px={3} 
                py={1}
                fontSize="sm"
                fontWeight="bold"
              >
                {nestedType === 'all' ? 'AND' : 'OR'} Group {level > 0 ? `(Level ${level + 1})` : ''}
              </Badge>
              <Text fontSize="xs" color="gray.600">
                {nestedConditions.length} condition{nestedConditions.length !== 1 ? 's' : ''}
              </Text>
            </HStack>
            <HStack spacing={2} flexWrap="wrap">
              <Button
                size="xs"
                colorScheme={nestedType === 'all' ? "blue" : "purple"}
                variant="outline"
                onClick={() => {
                  // Path to this nested group's array
                  const pathToGroupArray = [...parentPath, index, nestedType];
                  onAddCondition(pathToGroupArray);
                }}
                leftIcon={<AddIcon />}
                borderRadius="md"
                minW="120px"
              >
                Add Condition
              </Button>
              <Button
                size="xs"
                colorScheme="purple"
                variant="outline"
                onClick={() => {
                  // Path to this nested group's array
                  const pathToGroupArray = [...parentPath, index, nestedType];
                  onAddNested(pathToGroupArray);
                }}
                leftIcon={<AddIcon />}
                borderRadius="md"
                minW="110px"
              >
                Add Nested
              </Button>
              <IconButton
                icon={<DeleteIcon />}
                colorScheme="red"
                variant="outline"
                size="sm"
                onClick={() => {
                  // Path to remove this group from parent array
                  const pathToParent = [...parentPath, index];
                  onRemove(pathToParent);
                }}
                borderRadius="lg"
                aria-label="Remove group"
              />
            </HStack>
          </Flex>

          {/* Nested Conditions */}
          <VStack spacing={2} align="stretch">
            {nestedConditions.length === 0 ? (
              <Text fontSize="xs" color="gray.500" fontStyle="italic" textAlign="center" py={2}>
                No conditions in this group. Click "Add Condition" to add one.
              </Text>
            ) : (
              nestedConditions.map((nestedCondition, nestedIndex) => (
                <ConditionItem
                  key={nestedIndex}
                  condition={nestedCondition}
                  index={nestedIndex}
                  availableFacts={availableFacts}
                  onUpdate={onUpdate}
                  onRemove={onRemove}
                  onAddNested={onAddNested}
                  onAddCondition={onAddCondition}
                  parentPath={[...parentPath, index, nestedType]}
                  level={level + 1}
                  parentType={nestedType}
                />
              ))
            )}
          </VStack>
        </VStack>
      </Card>
    );
  }

  // Render simple condition
  return (
    <Card 
      bg="white" 
      borderRadius="md" 
      p={4} 
      border="1px solid" 
      borderColor="gray.300"
      ml={level * 4}
      position="relative"
    >
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs" color="gray.600" fontWeight="medium">Fact</FormLabel>
          <Select
            value={condition.fact || ''}
            onChange={(e) => onUpdate([...parentPath, index], 'fact', e.target.value)}
            bg="white"
            borderRadius="lg"
            size="sm"
            placeholder="Select fact..."
          >
            {availableFacts.map(fact => (
              <option key={fact} value={fact}>{fact}</option>
            ))}
          </Select>
          {!availableFacts.includes(condition.fact) && condition.fact && (
            <Input
              mt={2}
              value={condition.fact}
              onChange={(e) => onUpdate([...parentPath, index], 'fact', e.target.value)}
              placeholder="Or enter custom fact name..."
              bg="white"
              borderRadius="lg"
              size="sm"
              fontSize="xs"
            />
          )}
        </FormControl>
        
        <FormControl>
          <FormLabel fontSize="xs" color="gray.600" fontWeight="medium">Operator</FormLabel>
          <Select
            value={condition.operator || 'equal'}
            onChange={(e) => onUpdate([...parentPath, index], 'operator', e.target.value)}
            bg="white"
            borderRadius="lg"
            size="sm"
          >
            <option value="equal">Equal</option>
            <option value="notEqual">Not Equal</option>
            <option value="contains">Contains</option>
            <option value="notContains">Not Contains</option>
            <option value="in">In</option>
            <option value="notIn">Not In</option>
            <option value="greaterThan">Greater Than</option>
            <option value="lessThan">Less Than</option>
            <option value="greaterThanInclusive">Greater Than or Equal</option>
            <option value="lessThanInclusive">Less Than or Equal</option>
          </Select>
        </FormControl>
        
        <FormControl>
          <FormLabel fontSize="xs" color="gray.600" fontWeight="medium">Value</FormLabel>
          <Input
            value={condition.value || ''}
            onChange={(e) => onUpdate([...parentPath, index], 'value', e.target.value)}
            placeholder="Enter value"
            bg="white"
            borderRadius="lg"
            size="sm"
          />
        </FormControl>
        
        <FormControl display="flex" alignItems="flex-end">
          <IconButton
            icon={<DeleteIcon />}
            colorScheme="red"
            variant="outline"
            size="sm"
            onClick={() => onRemove([...parentPath, index])}
            borderRadius="lg"
            aria-label="Remove condition"
          />
        </FormControl>
      </SimpleGrid>
    </Card>
  );
};

// Rule Builder Component
const RuleBuilder = ({ rule, availableFacts, onSave, onCancel, onTest, isSaving }) => {
  const [ruleName, setRuleName] = useState(rule?.name || '');
  const [conditions, setConditions] = useState(
    rule?.conditions ? (typeof rule.conditions === 'string' ? JSON.parse(rule.conditions) : rule.conditions) : { all: [] }
  );
  const [event, setEvent] = useState(
    rule?.event ? (typeof rule.event === 'string' ? JSON.parse(rule.event) : rule.event) : { type: 'getOutcome', params: {} }
  );
  const [conditionType, setConditionType] = useState('all'); // 'all', 'any', or nested

  // Helper function to navigate to a target array by path
  // Path format: [index1, type1, index2, type2, ...] where type is 'all' or 'any'
  const navigateToArray = (obj, path, rootType) => {
    let current = obj[rootType] || [];
    let currentType = rootType;
    let i = 0;

    while (i < path.length) {
      const index = path[i];
      
      // Should be a number (index)
      if (typeof index !== 'number') {
        return null;
      }

      // Navigate into array item
      if (!Array.isArray(current) || !current[index]) {
        return null;
      }

      const item = current[index];
      
      // Check if item is a nested group
      if (item.all || item.any) {
        currentType = item.all ? 'all' : 'any';
        current = item[currentType];
        
        // Check if there's a type specified next (should match)
        if (i + 1 < path.length && (path[i + 1] === 'all' || path[i + 1] === 'any')) {
          if (path[i + 1] !== currentType) {
            return null; // Type mismatch
          }
          i += 2; // Skip both index and type
        } else {
          i += 1; // Just skip the index
        }
      } else {
        // Simple condition, can't navigate further
        return null;
      }
    }

    return { array: current, type: currentType };
  };

  // Helper function to update nested condition by path
  const updateConditionByPath = (path, field, value) => {
    const updated = JSON.parse(JSON.stringify(conditions));
    
    if (path.length === 0) {
      return;
    }

    const target = navigateToArray(updated, path.slice(0, -1), conditionType);
    if (!target || !Array.isArray(target.array)) return;

    const lastIndex = path[path.length - 1];
    if (target.array[lastIndex]) {
      target.array[lastIndex] = { ...target.array[lastIndex], [field]: value };
      setConditions(updated);
    }
  };

  // Helper function to remove condition by path
  const removeConditionByPath = (path) => {
    const updated = JSON.parse(JSON.stringify(conditions));
    
    if (path.length === 0) return;

    const target = navigateToArray(updated, path.slice(0, -1), conditionType);
    if (!target || !Array.isArray(target.array)) return;

    const targetIndex = path[path.length - 1];
    target.array.splice(targetIndex, 1);
    setConditions(updated);
  };

  // Helper function to add condition by path
  const addConditionByPath = (path) => {
    const newCondition = {
      fact: '',
      operator: 'equal',
      value: ''
    };

    const updated = JSON.parse(JSON.stringify(conditions));
    
    if (path.length === 0) {
      // Top level
      if (!updated[conditionType]) updated[conditionType] = [];
      updated[conditionType].push(newCondition);
      setConditions(updated);
      return;
    }

    const target = navigateToArray(updated, path, conditionType);
    if (!target || !Array.isArray(target.array)) return;

    target.array.push(newCondition);
    setConditions(updated);
  };

  // Helper function to add nested group by path
  const addNestedGroupByPath = (path) => {
    const updated = JSON.parse(JSON.stringify(conditions));
    
    if (path.length === 0) {
      // Top level
      if (!updated[conditionType]) updated[conditionType] = [];
      const nestedType = conditionType === 'all' ? 'any' : 'all';
      updated[conditionType].push({ [nestedType]: [] });
      setConditions(updated);
      return;
    }

    const target = navigateToArray(updated, path, conditionType);
    if (!target || !Array.isArray(target.array)) return;

    const nestedType = target.type === 'all' ? 'any' : 'all';
    target.array.push({ [nestedType]: [] });
    setConditions(updated);
  };

  const addCondition = () => {
    addConditionByPath([]);
  };

  const addNestedGroup = () => {
    addNestedGroupByPath([]);
  };

  const handleSave = () => {
    const ruleData = {
      name: ruleName,
      conditions: conditions,
      event: event
    };
    onSave(ruleData);
  };

  return (
    <Card mb={6} borderRadius="xl" shadow="lg" border="2px solid" borderColor="blue.200" w="full" maxW="100%">
      <CardHeader bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" borderTopRadius="xl">
        <Flex justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="bold" color="white" fontFamily="Sora, sans-serif">
            {rule ? 'Edit Rule' : 'Create New Rule'}
          </Text>
          <Button size="sm" variant="ghost" onClick={onCancel} color="white" _hover={{ bg: 'whiteAlpha.200' }}>
            <CloseIcon />
          </Button>
        </Flex>
      </CardHeader>

      <CardBody pt={6}>
        <VStack spacing={6} align="stretch">
          {/* Rule Name */}
          <FormControl isRequired>
            <FormLabel fontWeight="semibold" color="gray.700">Rule Name</FormLabel>
            <Input
              value={ruleName}
              onChange={(e) => setRuleName(e.target.value)}
              placeholder="e.g., HTN-ONBOARDING-STEP4-RULE"
              bg="white"
              border="1px solid"
              borderColor="gray.300"
              borderRadius="lg"
              _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
            />
          </FormControl>

          {/* Conditions Builder */}
          <Box>
            <FormLabel fontWeight="semibold" color="gray.700" mb={4}>Conditions</FormLabel>
            
            <Card bg="gray.50" borderRadius="lg" p={4} mb={4}>
              {/* Header with Condition Type and Action Buttons */}
              <Flex 
                direction={{ base: "column", md: "row" }} 
                gap={4} 
                mb={4} 
                align={{ base: "stretch", md: "flex-end" }}
                wrap="wrap"
              >
                <FormControl flex={{ base: "1", md: "0 0 200px" }} minW="200px">
                  <FormLabel fontSize="sm" color="gray.600" fontWeight="medium">Top Level Condition Type</FormLabel>
                  <Select
                    value={conditionType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      if (!conditions[newType]) {
                        setConditions({ ...conditions, [newType]: [] });
                      }
                      setConditionType(newType);
                    }}
                    bg="white"
                    borderRadius="lg"
                    size="md"
                  >
                    <option value="all">ALL (AND)</option>
                    <option value="any">ANY (OR)</option>
                  </Select>
                </FormControl>
                <HStack spacing={2} flex="1" flexWrap="wrap">
                  <Button
                    leftIcon={<AddIcon />}
                    colorScheme="blue"
                    variant="outline"
                    onClick={addCondition}
                    borderRadius="lg"
                    size="md"
                    flex={{ base: "1 1 100%", sm: "0 1 auto" }}
                    minW="140px"
                  >
                    Add Condition
                  </Button>
                  <Button
                    leftIcon={<AddIcon />}
                    colorScheme="purple"
                    variant="outline"
                    onClick={addNestedGroup}
                    borderRadius="lg"
                    size="md"
                    flex={{ base: "1 1 100%", sm: "0 1 auto" }}
                    minW="160px"
                  >
                    Add Nested Group
                  </Button>
                </HStack>
              </Flex>

              {/* Conditions List with Recursive Rendering */}
              <VStack spacing={3} align="stretch">
                {(conditions[conditionType] || []).length === 0 ? (
                  <Card bg="white" borderRadius="md" p={6} border="2px dashed" borderColor="gray.300">
                    <Text fontSize="sm" color="gray.500" textAlign="center" fontStyle="italic">
                      No conditions yet. Click "Add Condition" to create your first condition.
                    </Text>
                  </Card>
                ) : (
                  (conditions[conditionType] || []).map((condition, index) => (
                    <ConditionItem
                      key={index}
                      condition={condition}
                      index={index}
                      availableFacts={availableFacts}
                      onUpdate={updateConditionByPath}
                      onRemove={removeConditionByPath}
                      onAddNested={addNestedGroupByPath}
                      onAddCondition={addConditionByPath}
                      parentPath={[]}
                      level={0}
                      parentType={conditionType}
                    />
                  ))
                )}
              </VStack>

              {/* JSON Preview */}
              <Box mt={4} p={4} bg="gray.800" borderRadius="lg" fontFamily="mono" fontSize="xs" color="green.300" maxH="200px" overflowY="auto">
                <Text whiteSpace="pre-wrap">{JSON.stringify(conditions, null, 2)}</Text>
              </Box>
            </Card>
          </Box>

          {/* Event/Outcome */}
          <Box>
            <FormLabel fontWeight="semibold" color="gray.700" mb={4}>Event/Outcome</FormLabel>
            
            <Card bg="green.50" borderRadius="lg" p={4}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.600">Event Type</FormLabel>
                  <Input
                    value={event.type || ''}
                    onChange={(e) => setEvent({ ...event, type: e.target.value })}
                    placeholder="e.g., getOutcome"
                    bg="white"
                    borderRadius="lg"
                    size="sm"
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.600">Params (JSON)</FormLabel>
                  <Textarea
                    value={JSON.stringify(event.params || {}, null, 2)}
                    onChange={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        setEvent({ ...event, params: parsed });
                      } catch (err) {
                        // Invalid JSON, keep as is
                      }
                    }}
                    placeholder='{"contentId": "29"}'
                    bg="white"
                    borderRadius="lg"
                    fontFamily="mono"
                    fontSize="xs"
                    rows={4}
                  />
                </FormControl>
              </SimpleGrid>
              
              <Box p={3} bg="gray.800" borderRadius="md" fontFamily="mono" fontSize="xs" color="green.300" maxH="150px" overflowY="auto">
                <Text whiteSpace="pre-wrap">{JSON.stringify(event, null, 2)}</Text>
              </Box>
            </Card>
          </Box>

          {/* Action Buttons */}
          <HStack spacing={3} justify="flex-end" pt={6} borderTop="2px solid" borderColor="gray.200" mt={6}>
            <Button
              variant="outline"
              colorScheme="red"
              size="lg"
              onClick={onCancel}
              borderRadius="lg"
              leftIcon={<CloseIcon />}
              px={8}
            >
              Cancel
            </Button>
            <Button
              leftIcon={<ViewIcon />}
              colorScheme="green"
              variant="outline"
              size="lg"
              onClick={() => onTest(conditions, {})}
              borderRadius="lg"
              px={8}
            >
              Test Rule
            </Button>
            <Button
              colorScheme="blue"
              size="lg"
              onClick={handleSave}
              isLoading={isSaving}
              loadingText="Saving..."
              borderRadius="lg"
              isDisabled={!ruleName || !conditions}
              px={8}
            >
              {rule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );
};

// Rule Tester Component
const RuleTester = ({ rule, availableFacts, testFacts, setTestFacts, testResult, isTesting, onTest, onClose, isStandalone = false }) => {
  const [customFact, setCustomFact] = useState({ key: '', value: '' });

  const addFact = (factKey, factValue) => {
    setTestFacts({ ...testFacts, [factKey]: factValue });
    setCustomFact({ key: '', value: '' });
  };

  const removeFact = (factKey) => {
    const updated = { ...testFacts };
    delete updated[factKey];
    setTestFacts(updated);
  };

  const updateFact = (factKey, value) => {
    setTestFacts({ ...testFacts, [factKey]: value });
  };

  return (
    <Card mb={6} borderRadius="xl" shadow="lg" border="2px solid" borderColor="green.200" w="full" maxW="100%">
      <CardHeader bg="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" borderTopRadius="xl">
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={0}>
            <Text fontSize="lg" fontWeight="bold" color="white" fontFamily="Sora, sans-serif">
              {isStandalone ? 'Test Rule' : `Test Rule: ${rule?.name || 'Untitled'}`}
            </Text>
            <Text fontSize="sm" color="white" opacity={0.9}>
              {isStandalone 
                ? 'Test rule conditions with sample facts and see the outcome'
                : 'Test your rule with sample facts'}
            </Text>
          </VStack>
          <Button size="sm" variant="ghost" onClick={onClose} color="white" _hover={{ bg: 'whiteAlpha.200' }}>
            <CloseIcon />
          </Button>
        </Flex>
      </CardHeader>

      <CardBody pt={6}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
          {/* Facts Input */}
          <Box>
            <Text fontWeight="semibold" color="gray.700" mb={4}>Test Facts</Text>
            
            <Card bg="blue.50" borderRadius="lg" p={4} mb={4}>
              <VStack spacing={3} align="stretch">
                {/* Quick Facts from Available */}
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.600">Add Quick Fact</FormLabel>
                  <HStack spacing={2}>
                    <Select
                      placeholder="Select fact..."
                      bg="white"
                      borderRadius="lg"
                      size="sm"
                      onChange={(e) => {
                        if (e.target.value) {
                          const fact = e.target.value;
                          if (!testFacts[fact]) {
                            addFact(fact, '');
                          }
                          e.target.value = '';
                        }
                      }}
                    >
                      {availableFacts.filter(f => !testFacts[f]).map(fact => (
                        <option key={fact} value={fact}>{fact}</option>
                      ))}
                    </Select>
                  </HStack>
                </FormControl>

                {/* Custom Fact */}
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.600">Add Custom Fact</FormLabel>
                  <HStack spacing={2}>
                    <Input
                      placeholder="Fact key"
                      value={customFact.key}
                      onChange={(e) => setCustomFact({ ...customFact, key: e.target.value })}
                      bg="white"
                      borderRadius="lg"
                      size="sm"
                    />
                    <Input
                      placeholder="Fact value"
                      value={customFact.value}
                      onChange={(e) => setCustomFact({ ...customFact, value: e.target.value })}
                      bg="white"
                      borderRadius="lg"
                      size="sm"
                    />
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={() => {
                        if (customFact.key && customFact.value !== undefined) {
                          addFact(customFact.key, customFact.value);
                        }
                      }}
                      borderRadius="lg"
                    >
                      Add
                    </Button>
                  </HStack>
                </FormControl>

                {/* Current Facts */}
                <Divider />
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">Current Facts:</Text>
                
                {Object.keys(testFacts).length === 0 ? (
                  <Text fontSize="sm" color="gray.500" fontStyle="italic">
                    No facts added. Add facts above to test the rule.
                  </Text>
                ) : (
                  <VStack spacing={2} align="stretch">
                    {Object.entries(testFacts).map(([key, value]) => (
                      <Card key={key} bg="white" p={3} borderRadius="md">
                        <HStack justify="space-between">
                          <VStack align="start" spacing={1} flex={1}>
                            <Text fontSize="sm" fontWeight="semibold" color="blue.600">{key}</Text>
                            <Input
                              value={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              onChange={(e) => {
                                try {
                                  const parsed = JSON.parse(e.target.value);
                                  updateFact(key, parsed);
                                } catch {
                                  updateFact(key, e.target.value);
                                }
                              }}
                              size="sm"
                              bg="gray.50"
                              borderRadius="md"
                              fontFamily="mono"
                              fontSize="xs"
                            />
                          </VStack>
                          <IconButton
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => removeFact(key)}
                            borderRadius="lg"
                          />
                        </HStack>
                      </Card>
                    ))}
                  </VStack>
                )}
              </VStack>
            </Card>

          </Box>

          {/* Test Results */}
          <Box>
            <Text fontWeight="semibold" color="gray.700" mb={4}>Test Results</Text>
            
            {testResult ? (
              <Card bg={testResult.matched ? "green.50" : "red.50"} borderRadius="lg" p={4}>
                <VStack spacing={4} align="stretch">
                  <Alert status={testResult.matched ? "success" : "error"} borderRadius="lg">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>{testResult.matched ? "Rule Matched!" : "Rule Not Matched"}</AlertTitle>
                      {testResult.error && (
                        <AlertDescription>{testResult.error}</AlertDescription>
                      )}
                    </Box>
                  </Alert>

                  {testResult.matched && testResult.outcome && (
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>Outcome:</Text>
                      <Box p={3} bg="gray.800" borderRadius="md" fontFamily="mono" fontSize="xs" color="green.300" maxH="300px" overflowY="auto">
                        <Text whiteSpace="pre-wrap">{JSON.stringify(testResult.outcome, null, 2)}</Text>
                      </Box>
                    </Box>
                  )}

                  {testResult.analysis && (
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>Condition Analysis:</Text>
                      <Box p={3} bg="gray.800" borderRadius="md" fontFamily="mono" fontSize="xs" color="blue.300" maxH="200px" overflowY="auto">
                        <Text whiteSpace="pre-wrap">{JSON.stringify(testResult.analysis, null, 2)}</Text>
                      </Box>
                    </Box>
                  )}
                </VStack>
              </Card>
            ) : (
              <Card bg="gray.50" borderRadius="lg" p={8} textAlign="center">
                <VStack spacing={2}>
                  <Text color="gray.500" fontSize="sm">
                    {Object.keys(testFacts).length === 0
                      ? "Add facts and click 'Run Test' to see results"
                      : "Click 'Run Test' to evaluate the rule"}
                  </Text>
                </VStack>
              </Card>
            )}
          </Box>
        </SimpleGrid>
        
        {/* Action Buttons - Run Test and Cancel side by side on the right */}
        <Box mt={6} pt={6} borderTop="2px solid" borderColor="gray.200">
          <Flex justify="flex-end" gap={3}>
            <Button
              variant="outline"
              colorScheme="red"
              size="lg"
              onClick={onClose}
              borderRadius="lg"
              leftIcon={<CloseIcon />}
              px={8}
            >
              Cancel & Close
            </Button>
            <Button
              leftIcon={<CheckIcon />}
              colorScheme="green"
              size="lg"
              onClick={onTest}
              isLoading={isTesting}
              loadingText="Testing..."
              borderRadius="lg"
              px={8}
            >
              Run Test
            </Button>
          </Flex>
        </Box>
      </CardBody>
    </Card>
  );
};

const WorkflowConfigEditor = () => {
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [editingTaskRef, setEditingTaskRef] = useState(null); // Will store { categoryIndex, taskIndex }
  const [editingField, setEditingField] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [addingNewItem, setAddingNewItem] = useState({ type: null, data: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: '', index: null, fieldKey: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isEditingPageConfig, setIsEditingPageConfig] = useState(false);
  const [pageConfigTemp, setPageConfigTemp] = useState({ title: '', description: '', showLogs: false });
  const [activeSection, setActiveSection] = useState('overview');
  const [showOnlySection, setShowOnlySection] = useState(null); // Controls which section to show exclusively
  const [patientContextPatientId, setPatientContextPatientId] = useState('');
  const [patientContextLoading, setPatientContextLoading] = useState(false);
  const [patientContextSaving, setPatientContextSaving] = useState(false);
  const [patientContextForm, setPatientContextForm] = useState({
    firstName: '',
    lastName: '',
    birthDay: '',
    gender: '',
    phoneNumber: '',
    language: '',
    email: '',
    isTestAccount: false
  });
  const [patientPrograms, setPatientPrograms] = useState([]);
  
  // Rule Engine State
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [isCreatingRule, setIsCreatingRule] = useState(false);
  const [ruleFilter, setRuleFilter] = useState('');
  const [ruleSortBy, setRuleSortBy] = useState('name');
  const [ruleSortOrder, setRuleSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rulesPerPage] = useState(10);
  const [testingRule, setTestingRule] = useState(null);
  const [testFacts, setTestFacts] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [expandedRuleId, setExpandedRuleId] = useState(null);
  const [deleteRuleConfirm, setDeleteRuleConfirm] = useState({ show: false, rule: null });
  const [availableFacts, setAvailableFacts] = useState([
    'taskRefId', 'stepId', 'consent', 'shipping', 'taskStatus', 
    'bmi', 'programs', 'labs', 'patientStatus', 'selfEfficacyQuiz',
    'programCount', 'platform', 'remainingProgramsToSelect', 
    'hasCGM', 'hasInsulinPump', 'healthyPregnancy',
    'selfEfficacy', 'vpaccPatient', 'cuffSize'
  ]);

  const { colorMode, toggleColorMode } = useColorMode();
  const toast = useToast();
  
  const bg = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');

  // Load configuration on component mount
  useEffect(() => {
    loadConfiguration();
    loadRules();
  }, []);

  const loadConfiguration = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(`${appConfig.APIBaseURL}/api/v1/config/1`);
      setConfig(response.data);
    } catch (err) {
      setError('Failed to load configuration: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const saveToDatabase = async () => {
    try {
      setIsSaving(true);
      
      const response = await api.put(
        `${appConfig.APIBaseURL}/api/v1/config/1/workflow`,
        {
          workflowConfiguration: config.workflowConfiguration
        }
      );
      
      
      toast({
        title: "Configuration saved!",
        description: "Your workflow configuration has been successfully updated.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
    } catch (err) {
      console.error('Error saving configuration:', err);
      toast({
        title: "Save failed",
        description: 'Failed to save configuration: ' + (err.response?.data?.error || err.message),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const loadPatientContext = async (patientIdInput) => {
    const normalizedPatientId = String(patientIdInput || '').trim();
    if (!normalizedPatientId) {
      toast({
        title: 'Patient ID required',
        description: 'Enter a patient ID to load context.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      setPatientContextLoading(true);
      const response = await api.get(`${appConfig.APIBaseURL}/api/v1/config/1/patient-context/${encodeURIComponent(normalizedPatientId)}`);
      const payload = response.data || {};

      setPatientContextPatientId(normalizedPatientId);
      setPatientContextForm({
        firstName: payload?.patient?.firstName || '',
        lastName: payload?.patient?.lastName || '',
        birthDay: payload?.patient?.birthDay || '',
        gender: payload?.patient?.gender || '',
        phoneNumber: payload?.patient?.phoneNumber || '',
        language: payload?.patient?.language || '',
        email: payload?.patient?.email || '',
        isTestAccount: !!payload?.patient?.isTestAccount
      });
      setPatientPrograms(Array.isArray(payload?.programs) ? payload.programs : []);

      toast({
        title: 'Patient context loaded',
        description: `Loaded ${normalizedPatientId}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Error loading patient context:', err);
      toast({
        title: 'Load failed',
        description: err.response?.data?.error || err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPatientContextLoading(false);
    }
  };

  const setPatientProgramState = (index, key, isChecked) => {
    setPatientPrograms((prev) => prev.map((program, programIndex) => {
      if (programIndex !== index) return program;

      if (key === 'isEnrolled') {
        if (isChecked) {
          return { ...program, isEnrolled: true, isSelected: false };
        }
        return { ...program, isEnrolled: false };
      }

      if (isChecked) {
        return { ...program, isEnrolled: false, isSelected: true };
      }
      return { ...program, isSelected: false };
    }));
  };

  const savePatientContext = async () => {
    const normalizedPatientId = String(patientContextPatientId || '').trim();
    if (!normalizedPatientId) {
      toast({
        title: 'Patient ID required',
        description: 'Load a patient context first.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const invalidProgram = patientPrograms.find((program) => program.isEnrolled && program.isSelected);
    if (invalidProgram) {
      toast({
        title: 'Program state invalid',
        description: `${invalidProgram.programCode || invalidProgram.name}: IsEnrolled and IsSelected cannot both be checked.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setPatientContextSaving(true);

      await api.put(
        `${appConfig.APIBaseURL}/api/v1/config/1/patient-context/${encodeURIComponent(normalizedPatientId)}`,
        {
          patient: patientContextForm,
          programs: patientPrograms.map((program) => ({
            programId: program.programId,
            isEnrolled: !!program.isEnrolled,
            isSelected: !!program.isSelected
          }))
        }
      );

      toast({
        title: 'Patient context saved',
        description: 'Patient fields and program flags were updated.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      await loadPatientContext(normalizedPatientId);
    } catch (err) {
      console.error('Error saving patient context:', err);
      toast({
        title: 'Save failed',
        description: err.response?.data?.details || err.response?.data?.error || err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPatientContextSaving(false);
    }
  };

  // Rule Engine Functions
  const loadRules = async () => {
    try {
      setRulesLoading(true);
      const response = await api.get(`${appConfig.APIBaseURL}/api/v1/rules`);
      // Parse conditions and event if they come as strings
      const parsedRules = (response.data || []).map(rule => ({
        ...rule,
        conditions: typeof rule.conditions === 'string' ? JSON.parse(rule.conditions) : rule.conditions,
        event: typeof rule.event === 'string' ? JSON.parse(rule.event) : rule.event
      }));
      setRules(parsedRules);
    } catch (err) {
      console.error('Error loading rules:', err);
      toast({
        title: "Failed to load rules",
        description: err.response?.data?.error || err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRulesLoading(false);
    }
  };

  const createRule = async (ruleData) => {
    try {
      setIsSaving(true);
      const response = await api.post(`${appConfig.APIBaseURL}/api/v1/rules/create`, ruleData);
      toast({
        title: "Rule created!",
        description: "Your rule has been successfully created.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      await loadRules();
      setIsCreatingRule(false);
      return response.data;
    } catch (err) {
      console.error('Error creating rule:', err);
      toast({
        title: "Failed to create rule",
        description: err.response?.data?.error || err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const updateRule = async (ruleId, ruleData) => {
    try {
      setIsSaving(true);
      // The API expects ruleName, but we're sending name, so let's map it
      await api.put(`${appConfig.APIBaseURL}/api/v1/rules/${ruleId}`, {
        ruleName: ruleData.name,
        conditions: ruleData.conditions,
        event: ruleData.event
      });
      toast({
        title: "Rule updated!",
        description: "Your rule has been successfully updated.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      await loadRules();
      setEditingRule(null);
    } catch (err) {
      console.error('Error updating rule:', err);
      toast({
        title: "Failed to update rule",
        description: err.response?.data?.error || err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      setIsSaving(true);
      await api.delete(`${appConfig.APIBaseURL}/api/v1/rules/${ruleId}`);
      toast({
        title: "Rule deleted!",
        description: "Your rule has been successfully deleted.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      await loadRules();
    } catch (err) {
      console.error('Error deleting rule:', err);
      toast({
        title: "Failed to delete rule",
        description: err.response?.data?.error || err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const testRule = async (ruleConditions, facts) => {
    try {
      setIsTesting(true);
      setTestResult(null);
      
      // Try to test the rule - if endpoint doesn't exist, provide mock result
      try {
        const response = await api.post(`${appConfig.APIBaseURL}/api/v1/rules/test`, {
          conditions: ruleConditions,
          facts: facts
        });
        setTestResult(response.data);
      } catch (testErr) {
        // If test endpoint doesn't exist, create a simple evaluation
        // This is a client-side basic evaluation for demonstration
        const matched = evaluateConditionsBasic(ruleConditions, facts);
        setTestResult({
          matched: matched,
          outcome: matched ? { message: 'Rule would match with these facts' } : null,
          analysis: { 
            conditions: ruleConditions,
            facts: facts,
            result: matched
          }
        });
      }
    } catch (err) {
      console.error('Error testing rule:', err);
      setTestResult({
        matched: false,
        error: err.response?.data?.error || err.message
      });
    } finally {
      setIsTesting(false);
    }
  };

  // Basic condition evaluation for testing (client-side)
  const evaluateConditionsBasic = (conditions, facts) => {
    if (!conditions) return false;
    
    if (conditions.all && Array.isArray(conditions.all)) {
      return conditions.all.every(condition => {
        if (condition.all || condition.any) {
          return evaluateConditionsBasic(condition, facts);
        }
        const factValue = facts[condition.fact];
        const operator = condition.operator || 'equal';
        const expectedValue = condition.value;
        
        switch (operator) {
          case 'equal':
            return String(factValue) === String(expectedValue);
          case 'notEqual':
            return String(factValue) !== String(expectedValue);
          case 'contains':
            return String(factValue).includes(String(expectedValue));
          case 'notContains':
            return !String(factValue).includes(String(expectedValue));
          default:
            return String(factValue) === String(expectedValue);
        }
      });
    }
    
    if (conditions.any && Array.isArray(conditions.any)) {
      return conditions.any.some(condition => {
        if (condition.all || condition.any) {
          return evaluateConditionsBasic(condition, facts);
        }
        const factValue = facts[condition.fact];
        const operator = condition.operator || 'equal';
        const expectedValue = condition.value;
        
        switch (operator) {
          case 'equal':
            return String(factValue) === String(expectedValue);
          case 'notEqual':
            return String(factValue) !== String(expectedValue);
          case 'contains':
            return String(factValue).includes(String(expectedValue));
          case 'notContains':
            return !String(factValue).includes(String(expectedValue));
          default:
            return String(factValue) === String(expectedValue);
        }
      });
    }
    
    return false;
  };

  // Filter and sort rules
  const getFilteredAndSortedRules = () => {
    let filtered = rules;
    
    // Apply filter
    if (ruleFilter) {
      const filterLower = ruleFilter.toLowerCase();
      filtered = rules.filter(rule => {
        const taskRefId = extractTaskRefId(rule.conditions);
        return rule.name?.toLowerCase().includes(filterLower) ||
          taskRefId?.toLowerCase().includes(filterLower) ||
          JSON.stringify(rule.conditions).toLowerCase().includes(filterLower) ||
          JSON.stringify(rule.event).toLowerCase().includes(filterLower);
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      if (ruleSortBy === 'taskRefId') {
        aVal = extractTaskRefId(a.conditions);
        bVal = extractTaskRefId(b.conditions);
      } else {
        aVal = a[ruleSortBy] || '';
        bVal = b[ruleSortBy] || '';
      }
      
      const comparison = aVal.toString().localeCompare(bVal.toString());
      return ruleSortOrder === 'asc' ? comparison : -comparison;
    });
    
    // Apply pagination
    const startIndex = (currentPage - 1) * rulesPerPage;
    const endIndex = startIndex + rulesPerPage;
    
    return {
      filtered: filtered.slice(startIndex, endIndex),
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / rulesPerPage)
    };
  };

  const handleSort = (column) => {
    if (ruleSortBy === column) {
      setRuleSortOrder(ruleSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setRuleSortBy(column);
      setRuleSortOrder('asc');
    }
  };

  // Extract taskRefId from conditions
  const extractTaskRefId = (conditions) => {
    if (!conditions) return 'N/A';
    
    const traverse = (obj) => {
      if (Array.isArray(obj)) {
        for (const item of obj) {
          const result = traverse(item);
          if (result) return result;
        }
      } else if (typeof obj === 'object' && obj !== null) {
        if (obj.fact === 'taskRefId' && obj.value) {
          return obj.value;
        }
        for (const value of Object.values(obj)) {
          const result = traverse(value);
          if (result) return result;
        }
      }
      return null;
    };
    
    return traverse(conditions) || 'N/A';
  };

  // Task Reference Management
  const addTaskReference = () => {
    const newTaskRef = {
      label: "New Task Reference",
      value: "NEW-TASK-REF",
      default: false,
      categoryIndex: 0, // Default to first category
      metadata: {
        visibleFields: [],
        requiredFields: [],
        validationRules: {}
      }
    };

    setAddingNewItem({ type: 'taskRef', data: newTaskRef });
    setActiveTab('taskRefs');
  };

  // Category Management
  const addCategory = () => {
    const newCategory = {
      name: "New Category",
      icon: "📋",
      description: "Category description",
      options: []
    };
    setAddingNewItem({ type: 'category', data: newCategory });
    setActiveTab('categories');
    setShowOnlySection('categories');
  };

  const saveNewCategory = async () => {
      // Create the updated configuration
      const updatedConfig = {
        ...config,
        workflowConfiguration: {
          ...config.workflowConfiguration,
          taskReference: {
            ...config.workflowConfiguration.taskReference,
            categories: [...(config.workflowConfiguration.taskReference.categories || []), addingNewItem.data]
          }
        }
      };
      
      // Update local state
      setConfig(updatedConfig);
      
      // Save to database with the updated configuration
      try {
        setIsSaving(true);
        
        const response = await api.put(
          `${appConfig.APIBaseURL}/api/v1/config/1/workflow`,
          {
            workflowConfiguration: updatedConfig.workflowConfiguration
          }
        );
        
        
        toast({
          title: "Configuration saved!",
          description: "Your workflow configuration has been successfully updated.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        
      } catch (err) {
        console.error('Error saving configuration:', err);
        toast({
          title: "Save failed",
          description: 'Failed to save configuration: ' + (err.response?.data?.error || err.message),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsSaving(false);
      }
      
      setAddingNewItem({ type: null, data: null });
      if (showOnlySection === 'taskRefs') {
        returnToOverview();
      }
  };

  const cancelNewCategory = () => {
    setAddingNewItem({ type: null, data: null });
    if (showOnlySection === 'taskRefs') {
      returnToOverview();
    }
  };

  const updateCategory = (categoryIndex, field, value) => {
    setConfig(prev => ({
      ...prev,
      workflowConfiguration: {
        ...prev.workflowConfiguration,
        taskReference: {
          ...prev.workflowConfiguration.taskReference,
          categories: prev.workflowConfiguration.taskReference.categories.map((category, i) =>
            i === categoryIndex ? { ...category, [field]: value } : category
          )
        }
      }
    }));
  };

  // Page Configuration Management
  const startEditingPageConfig = () => {
    setPageConfigTemp({
      title: config?.workflowConfiguration?.page?.title || '',
      description: config?.workflowConfiguration?.page?.description || '',
      showLogs: config?.workflowConfiguration?.page?.showLogs || false
    });
    setIsEditingPageConfig(true);
    setActiveSection('pageConfig');
  };

  const cancelEditingPageConfig = () => {
    setIsEditingPageConfig(false);
    setPageConfigTemp({ title: '', description: '', showLogs: false });
    if (showOnlySection === 'pageConfig') {
      returnToOverview();
    }
  };

  const savePageConfig = async () => {
    // Create the updated configuration
    const updatedConfig = {
      ...config,
      workflowConfiguration: {
        ...config.workflowConfiguration,
        page: {
          ...config.workflowConfiguration.page,
          title: pageConfigTemp.title,
          description: pageConfigTemp.description,
          showLogs: pageConfigTemp.showLogs
        }
      }
    };
    
    // Update local state
    setConfig(updatedConfig);
    
    // Save to database with the updated configuration
    try {
      setIsSaving(true);
      
      const response = await api.put(
        `${appConfig.APIBaseURL}/api/v1/config/1/workflow`,
        {
          workflowConfiguration: updatedConfig.workflowConfiguration
        }
      );
      
      
      toast({
        title: "Configuration saved!",
        description: "Your workflow configuration has been successfully updated.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
      // Close edit mode and navigate to overview (same as cancel)
      setIsEditingPageConfig(false);
      if (showOnlySection === 'pageConfig') {
        returnToOverview();
      }
      
    } catch (err) {
      console.error('Error saving configuration:', err);
      toast({
        title: "Save failed",
        description: 'Failed to save configuration: ' + (err.response?.data?.error || err.message),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Function to show only a specific section
  const showOnlySpecificSection = (section) => {
    setShowOnlySection(section);
    setActiveTab(section);
    setActiveSection(section);
  };

  // Function to return to overview (show all sections)
  const returnToOverview = () => {
    setShowOnlySection(null);
    setActiveTab('overview');
    setActiveSection('overview');
    setEditingTaskRef(null);
    setEditingField(null);
    setIsEditingPageConfig(false);
  };

  const updatePageConfig = (field, value) => {
    setConfig(prev => ({
      ...prev,
      workflowConfiguration: {
        ...prev.workflowConfiguration,
        page: {
          ...prev.workflowConfiguration.page,
          [field]: value
        }
      }
    }));
  };

  const removeCategory = (categoryIndex) => {
    setDeleteConfirm({
      type: 'category',
      index: categoryIndex,
      name: config.workflowConfiguration.taskReference.categories[categoryIndex].name
    });
  };

  const saveNewTaskReference = async () => {
    // Extract the task data without categoryIndex
    const { categoryIndex, ...taskData } = addingNewItem.data;
    
    // Create the updated configuration
    const updatedConfig = {
      ...config,
      workflowConfiguration: {
        ...config.workflowConfiguration,
        taskReference: {
          ...config.workflowConfiguration.taskReference,
          categories: config.workflowConfiguration.taskReference.categories.map((category, i) =>
            i === categoryIndex
              ? { ...category, options: [...(category.options || []), taskData] }
              : category
          )
        }
      }
    };
    
    // Update local state
    setConfig(updatedConfig);
    
    // Save to database with the updated configuration
    try {
      setIsSaving(true);
      
      const response = await api.put(
        `${appConfig.APIBaseURL}/api/v1/config/1/workflow`,
        {
          workflowConfiguration: updatedConfig.workflowConfiguration
        }
      );
      
      
      toast({
        title: "Configuration saved!",
        description: "Your workflow configuration has been successfully updated.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
    } catch (err) {
      console.error('Error saving configuration:', err);
      toast({
        title: "Save failed",
        description: 'Failed to save configuration: ' + (err.response?.data?.error || err.message),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
    
    setAddingNewItem({ type: null, data: null });
    if (showOnlySection === 'taskRefs') {
      returnToOverview();
    }
  };

  const cancelNewTaskReference = () => {
    setAddingNewItem({ type: null, data: null });
    if (showOnlySection === 'taskRefs') {
      returnToOverview();
    }
  };

  const updateTaskReference = (categoryIndex, taskIndex, field, value) => {
    setConfig(prev => ({
      ...prev,
      workflowConfiguration: {
        ...prev.workflowConfiguration,
        taskReference: {
          ...prev.workflowConfiguration.taskReference,
          categories: prev.workflowConfiguration.taskReference.categories.map((category, catIndex) =>
            catIndex === categoryIndex ? {
              ...category,
              options: category.options.map((option, optIndex) =>
                optIndex === taskIndex ? { ...option, [field]: value } : option
              )
            } : category
          )
        }
      }
    }));
  };
  const addUniqueField = (fields = [], fieldKey) => (
    fields.includes(fieldKey) ? fields : [...fields, fieldKey]
  );

  const getTaskRefVisibleFields = (taskRef) => {
    const visibleFields = Array.isArray(taskRef?.metadata?.visibleFields)
      ? taskRef.metadata.visibleFields
      : [];
    const requiredFields = Array.isArray(taskRef?.metadata?.requiredFields)
      ? taskRef.metadata.requiredFields
      : [];

    // Required fields are always considered visible.
    return [...new Set([...visibleFields, ...requiredFields])];
  };

  const getTaskRefRequiredFields = (taskRef) => (
    taskRef?.metadata?.requiredFields || []
  );

  const updateVisibleMetadata = (categoryIndex, taskIndex, fieldKey, isVisible) => {
    setConfig(prev => ({
      ...prev,
      workflowConfiguration: {
        ...prev.workflowConfiguration,
        taskReference: {
          ...prev.workflowConfiguration.taskReference,
          categories: prev.workflowConfiguration.taskReference.categories.map((category, catIndex) =>
            catIndex === categoryIndex ? {
              ...category,
              options: category.options.map((option, optIndex) => {
                if (optIndex !== taskIndex) {
                  return option;
                }

                const visibleFields = getTaskRefVisibleFields(option);
                const requiredFields = getTaskRefRequiredFields(option);

                return {
                  ...option,
                  metadata: {
                    ...option.metadata,
                    visibleFields: isVisible
                      ? addUniqueField(visibleFields, fieldKey)
                      : visibleFields.filter(field => field !== fieldKey),
                    requiredFields: isVisible
                      ? requiredFields
                      : requiredFields.filter(field => field !== fieldKey)
                  }
                };
              })
            } : category
          )
        }
      }
    }));
  };

  const updateRequiredMetadata = (categoryIndex, taskIndex, fieldKey, isRequired) => {
    setConfig(prev => ({
      ...prev,
      workflowConfiguration: {
        ...prev.workflowConfiguration,
        taskReference: {
          ...prev.workflowConfiguration.taskReference,
          categories: prev.workflowConfiguration.taskReference.categories.map((category, catIndex) =>
            catIndex === categoryIndex ? {
              ...category,
              options: category.options.map((option, optIndex) => {
                if (optIndex !== taskIndex) {
                  return option;
                }

                const visibleFields = getTaskRefVisibleFields(option);
                const requiredFields = getTaskRefRequiredFields(option);

                return {
                  ...option,
                  metadata: {
                    ...option.metadata,
                    visibleFields: isRequired
                      ? addUniqueField(visibleFields, fieldKey)
                      : visibleFields,
                    requiredFields: isRequired
                      ? addUniqueField(requiredFields, fieldKey)
                      : requiredFields.filter(field => field !== fieldKey)
                  }
                };
              })
            } : category
          )
        }
      }
    }));
  };

  const moveTaskToCategory = (fromCategoryIndex, taskIndex, toCategoryIndex) => {
    setConfig(prev => {
      const taskToMove = prev.workflowConfiguration.taskReference.categories[fromCategoryIndex].options[taskIndex];
      
      return {
        ...prev,
        workflowConfiguration: {
          ...prev.workflowConfiguration,
          taskReference: {
            ...prev.workflowConfiguration.taskReference,
            categories: prev.workflowConfiguration.taskReference.categories.map((category, catIndex) => {
              if (catIndex === fromCategoryIndex) {
                // Remove task from source category
                return {
                  ...category,
                  options: category.options.filter((_, optIndex) => optIndex !== taskIndex)
                };
              } else if (catIndex === toCategoryIndex) {
                // Add task to destination category
                return {
                  ...category,
                  options: [...category.options, taskToMove]
                };
              }
              return category;
            })
          }
        }
      };
    });
  };

  const updateNewTaskRefVisibleFields = (fieldKey, isVisible) => {
    setAddingNewItem(prev => ({
      ...prev,
      data: {
        ...prev.data,
        metadata: {
          ...prev.data.metadata,
          visibleFields: isVisible
            ? addUniqueField(getTaskRefVisibleFields(prev.data), fieldKey)
            : getTaskRefVisibleFields(prev.data).filter(field => field !== fieldKey),
          requiredFields: isVisible
            ? getTaskRefRequiredFields(prev.data)
            : getTaskRefRequiredFields(prev.data).filter(field => field !== fieldKey)
        }
      }
    }));
  };

  const updateNewTaskRefRequiredFields = (fieldKey, isRequired) => {
    setAddingNewItem(prev => ({
      ...prev,
      data: {
        ...prev.data,
        metadata: {
          ...prev.data.metadata,
          visibleFields: isRequired
            ? addUniqueField(getTaskRefVisibleFields(prev.data), fieldKey)
            : getTaskRefVisibleFields(prev.data),
          requiredFields: isRequired
            ? addUniqueField(getTaskRefRequiredFields(prev.data), fieldKey)
            : getTaskRefRequiredFields(prev.data).filter(field => field !== fieldKey)
        }
      }
    }));
  };

  const removeTaskReference = (categoryIndex, taskIndex) => {
    setDeleteConfirm({ show: true, type: 'taskRef', categoryIndex, taskIndex, fieldKey: null });
  };

  const confirmDelete = async () => {
    let updatedConfig;
    
    if (deleteConfirm.type === 'category') {
      updatedConfig = {
        ...config,
        workflowConfiguration: {
          ...config.workflowConfiguration,
          taskReference: {
            ...config.workflowConfiguration.taskReference,
            categories: config.workflowConfiguration.taskReference.categories.filter((_, i) => i !== deleteConfirm.index)
          }
        }
      };
    } else if (deleteConfirm.type === 'taskRef') {
      updatedConfig = {
        ...config,
        workflowConfiguration: {
          ...config.workflowConfiguration,
          taskReference: {
            ...config.workflowConfiguration.taskReference,
            categories: config.workflowConfiguration.taskReference.categories.map((category, catIndex) =>
              catIndex === deleteConfirm.categoryIndex ? {
                ...category,
                options: category.options.filter((_, optIndex) => optIndex !== deleteConfirm.taskIndex)
              } : category
            )
          }
        }
      };
    } else if (deleteConfirm.type === 'field') {
      const newWorkflowConfig = { ...config.workflowConfiguration };
      delete newWorkflowConfig[deleteConfirm.fieldKey];
      updatedConfig = {
        ...config,
        workflowConfiguration: newWorkflowConfig
      };
    }
    
    // Update local state
    setConfig(updatedConfig);
    
    // Save to database with the updated configuration
    try {
      setIsSaving(true);
      
      const response = await api.put(
        `${appConfig.APIBaseURL}/api/v1/config/1/workflow`,
        {
          workflowConfiguration: updatedConfig.workflowConfiguration
        }
      );
      
      
      toast({
        title: "Configuration saved!",
        description: "Your workflow configuration has been successfully updated.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
    } catch (err) {
      console.error('Error saving configuration:', err);
      toast({
        title: "Save failed",
        description: 'Failed to save configuration: ' + (err.response?.data?.error || err.message),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
    
    setDeleteConfirm({ show: false, type: '', index: null, fieldKey: null });
  };

  // Field Management
  const addField = () => {
    const newField = {
      label: "New Field",
      type: "single",
      required: false,
      defaultVisible: false,
      options: [
        {
          value: "option1",
          label: "Option 1",
          default: false
        }
      ],
      // Slider-specific properties
      min: 0,
      max: 100,
      defaultValue: 0,
      // E8 Scoring-specific properties
      e8Sliders: [
        { key: 'nicotineExposure', label: 'Nicotine Exposure', min: 0, max: 100, defaultValue: 75 },
        { key: 'physicalActivity', label: 'Physical Activity', min: 0, max: 100, defaultValue: 0 },
        { key: 'sleep', label: 'Sleep Quality', min: 0, max: 100, defaultValue: 0 },
        { key: 'eating', label: 'Eating Habits', min: 0, max: 100, defaultValue: 50 }
      ]
    };

    setAddingNewItem({ type: 'field', data: { fieldKey: '', field: newField } });
    setActiveTab('fields');
  };

  const saveNewField = async () => {
      // Create the updated configuration
      const updatedConfig = {
        ...config,
        workflowConfiguration: {
          ...config.workflowConfiguration,
          [addingNewItem.data.fieldKey]: addingNewItem.data.field
        }
      };
      
      // Update local state
      setConfig(updatedConfig);
      
      // Save to database with the updated configuration
      try {
        setIsSaving(true);
        
        const response = await api.put(
          `${appConfig.APIBaseURL}/api/v1/config/1/workflow`,
          {
            workflowConfiguration: updatedConfig.workflowConfiguration
          }
        );
        
        
        toast({
          title: "Configuration saved!",
          description: "Your workflow configuration has been successfully updated.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        
      } catch (err) {
        console.error('Error saving configuration:', err);
        toast({
          title: "Save failed",
          description: 'Failed to save configuration: ' + (err.response?.data?.error || err.message),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsSaving(false);
      }
      
      setAddingNewItem({ type: null, data: null });
      if (showOnlySection === 'fields') {
        returnToOverview();
      }
  };

  const cancelNewField = () => {
    setAddingNewItem({ type: null, data: null });
    if (showOnlySection === 'fields') {
      returnToOverview();
    }
  };

  const updateNewTaskRef = (field, value) => {
    setAddingNewItem(prev => ({
      ...prev,
      data: { ...prev.data, [field]: value }
    }));
  };

  const updateNewTaskRefMetadata = (field, value) => {
    setAddingNewItem(prev => ({
      ...prev,
      data: {
        ...prev.data,
        metadata: {
          ...prev.data.metadata,
          [field]: value
        }
      }
    }));
  };

  const updateNewField = (field, value) => {
    setAddingNewItem(prev => {
      const currentField = prev?.data?.field || {};
      const nextField = { ...currentField, [field]: value };

      // Required fields must always be visible.
      if (field === 'required' && value === true) {
        nextField.defaultVisible = true;
      }

      // If visibility is turned off, the field cannot remain required.
      if (field === 'defaultVisible' && value === false && nextField.required === true) {
        nextField.required = false;
      }

      return {
        ...prev,
        data: {
          ...prev.data,
          field: nextField
        }
      };
    });
  };

  const addFieldOption = (fieldKey) => {
    setConfig(prev => ({
      ...prev,
      workflowConfiguration: {
        ...prev.workflowConfiguration,
        [fieldKey]: {
          ...prev.workflowConfiguration[fieldKey],
          options: [
            ...(prev.workflowConfiguration[fieldKey].options || []),
            {
              value: `option${Date.now()}`,
              label: `Option ${(prev.workflowConfiguration[fieldKey].options?.length || 0) + 1}`,
              default: false
            }
          ]
        }
      }
    }));
  };

  const removeFieldOption = (fieldKey, optionIndex) => {
    setConfig(prev => ({
      ...prev,
      workflowConfiguration: {
        ...prev.workflowConfiguration,
        [fieldKey]: {
          ...prev.workflowConfiguration[fieldKey],
          options: prev.workflowConfiguration[fieldKey].options.filter((_, index) => index !== optionIndex)
        }
      }
    }));
  };

  const updateField = (fieldKey, field, value) => {
    setConfig(prev => {
      const currentField = prev?.workflowConfiguration?.[fieldKey] || {};
      const nextField = {
        ...currentField,
        [field]: value
      };

      // Required fields must always be visible.
      if (field === 'required' && value === true) {
        nextField.defaultVisible = true;
      }

      // If visibility is turned off, the field cannot remain required.
      if (field === 'defaultVisible' && value === false && nextField.required === true) {
        nextField.required = false;
      }

      return {
        ...prev,
        workflowConfiguration: {
          ...prev.workflowConfiguration,
          [fieldKey]: nextField
        }
      };
    });
  };

  const removeField = (fieldKey) => {
    setDeleteConfirm({ show: true, type: 'field', index: null, fieldKey });
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied to clipboard!",
        description: "Configuration has been copied to your clipboard.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      toast({
        title: "Copy failed",
        description: "Failed to copy to clipboard. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Search functionality
  const performSearch = (query) => {
    if (!query.trim() || !config?.workflowConfiguration) {
      setSearchResults(null);
      return;
    }

    const results = {
      taskReferences: [],
      fields: [],
      general: []
    };

    const searchTerm = query.toLowerCase();

    // Search in Task References (categorized structure)
    config.workflowConfiguration.taskReference?.categories?.forEach((category, categoryIndex) => {
      category.options?.forEach((taskRef, taskIndex) => {
      const matches = [];
      
        // Search in category info
        if (category.name?.toLowerCase().includes(searchTerm)) {
          matches.push(`Category: "${category.name}"`);
        }
        if (category.description?.toLowerCase().includes(searchTerm)) {
          matches.push(`Category Description: "${category.description}"`);
        }

        // Search in task info
      if (taskRef.label?.toLowerCase().includes(searchTerm)) {
        matches.push(`Label: "${taskRef.label}"`);
      }
      if (taskRef.value?.toLowerCase().includes(searchTerm)) {
        matches.push(`Value: "${taskRef.value}"`);
      }
      
      // Search in metadata
      Object.entries(taskRef.metadata || {}).forEach(([key, value]) => {
        if (key.toLowerCase().includes(searchTerm) || String(value).toLowerCase().includes(searchTerm)) {
          matches.push(`Metadata: ${key} = ${value}`);
        }
      });

      if (matches.length > 0) {
        results.taskReferences.push({
            categoryIndex,
            taskIndex,
            category,
          taskRef,
          matches
        });
      }
      });
    });

    // Search in Fields
    Object.entries(config.workflowConfiguration).forEach(([fieldKey, field]) => {
      if (fieldKey === 'taskReference' || fieldKey === 'page') return;
      
      const matches = [];
      
      if (fieldKey.toLowerCase().includes(searchTerm)) {
        matches.push(`Field Key: "${fieldKey}"`);
      }
      if (field.label?.toLowerCase().includes(searchTerm)) {
        matches.push(`Label: "${field.label}"`);
      }
      if (field.type?.toLowerCase().includes(searchTerm)) {
        matches.push(`Type: "${field.type}"`);
      }
      
      // Search in options
      field.options?.forEach((option, optionIndex) => {
        if (option.value?.toLowerCase().includes(searchTerm) || option.label?.toLowerCase().includes(searchTerm)) {
          matches.push(`Option ${optionIndex + 1}: "${option.value}" - "${option.label}"`);
        }
      });

      if (matches.length > 0) {
        results.fields.push({
          fieldKey,
          field,
          matches
        });
      }
    });

    setSearchResults(results);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    performSearch(query);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={8}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error!</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button mt={4} onClick={loadConfiguration}>Retry</Button>
      </Box>
    );
  }

  const availableFields = Object.keys(config?.workflowConfiguration || {}).filter(key => key !== 'taskReference' && key !== 'page');
  const getFieldDisplayLabel = (fieldKey) => {
    const configuredLabel = config?.workflowConfiguration?.[fieldKey]?.label;
    if (typeof configuredLabel === 'string' && configuredLabel.trim()) {
      return configuredLabel;
    }
    return fieldKey.replace(/([A-Z])/g, ' $1').trim();
  };

  // Calculate totals
  const taskRefCount = config?.workflowConfiguration?.taskReference?.categories?.reduce((total, category) => {
    return total + (category.options?.length || 0);
  }, 0) || 0;
  const categoryCount = config?.workflowConfiguration?.taskReference?.categories?.length || 0;
  const categories = config?.workflowConfiguration?.taskReference?.categories || [];

  const fieldCount = availableFields.length;

  return (
    <Box bg="gray.50" minH="100vh" fontFamily="Roboto, sans-serif">
      {/* Modern Header */}
      <Box bg="white" px={6} py={4} borderBottom="1px solid" borderColor="gray.200" shadow="sm">
        <Flex justify="space-between" align="center" maxW="1600px" mx="auto">
          <HStack spacing={4}>
            <Box bg="blue.500" p={2} borderRadius="lg">
              <StarIcon color="white" boxSize={6} />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold" color="gray.800" fontFamily="Sora, sans-serif">Workflow Configuration</Text>
              <Text fontSize="sm" color="gray.500" fontFamily="Roboto, sans-serif">Manage your workflow settings</Text>
            </VStack>
          </HStack>
          
          <HStack spacing={4}>
                         <InputGroup maxW="400px">
               <InputLeftElement pointerEvents="none">
                 <SearchIcon color="gray.400" />
               </InputLeftElement>
               <Input 
                 value={searchQuery}
                 onChange={handleSearchChange}
                 placeholder="Search configurations..." 
                 bg="gray.50" 
                 border="1px solid"
                 borderColor="gray.200"
                 borderRadius="lg"
                 _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px blue.400" }}
               />
               {searchQuery && (
                 <InputRightElement>
                   <IconButton
                     icon={<CloseIcon />}
                     size="sm"
                     variant="ghost"
                     onClick={clearSearch}
                     aria-label="Clear search"
                   />
                 </InputRightElement>
               )}
             </InputGroup>
            
            <IconButton
              icon={<BellIcon />}
              variant="ghost"
              aria-label="Notifications"
              borderRadius="lg"
            />
            
            
          </HStack>
        </Flex>
      </Box>

      <Flex maxW="1600px" mx="auto" minH="calc(100vh - 80px)">
        {/* Left Sidebar - Navigation */}
        <Box w="280px" bg="white" borderRight="1px solid" borderColor="gray.200" p={6}>
          <VStack spacing={6} align="stretch">
            {/* Navigation Menu */}
            <VStack spacing={2} align="stretch">
                             <Text fontSize="sm" fontWeight="bold" color="gray.600" textTransform="uppercase" letterSpacing="wide" fontFamily="Sora, sans-serif">
                 Navigation
               </Text>
              <VStack spacing={1} align="stretch">
                <Button
                  variant={activeTab === 'overview' ? 'solid' : 'ghost'}
                  colorScheme={activeTab === 'overview' ? 'blue' : 'gray'}
                  justifyContent="start"
                  leftIcon={<ViewIcon />}
                  onClick={returnToOverview}
                  borderRadius="lg"
                  h="48px"
                >
                  Dashboard Overview
                </Button>
                <Button
                  variant={activeTab === 'categories' ? 'solid' : 'ghost'}
                  colorScheme={activeTab === 'categories' ? 'purple' : 'gray'}
                  justifyContent="start"
                  leftIcon={<HamburgerIcon />}
                  onClick={() => showOnlySpecificSection('categories')}
                  borderRadius="lg"
                  h="48px"
                >
                  Categories
                  {categoryCount > 0 && (
                    <Badge ml={2} colorScheme="purple" borderRadius="full" fontSize="xs">{categoryCount}</Badge>
                  )}
                </Button>
                <Button
                  variant={activeTab === 'taskRefs' ? 'solid' : 'ghost'}
                  colorScheme={activeTab === 'taskRefs' ? 'blue' : 'gray'}
                  justifyContent="start"
                  leftIcon={<EditIcon />}
                  onClick={() => showOnlySpecificSection('taskRefs')}
                  borderRadius="lg"
                  h="48px"
                >
                  Task References
                </Button>
                <Button
                  variant={activeTab === 'fields' ? 'solid' : 'ghost'}
                  colorScheme={activeTab === 'fields' ? 'blue' : 'gray'}
                  justifyContent="start"
                  leftIcon={<SettingsIcon />}
                  onClick={() => showOnlySpecificSection('fields')}
                  borderRadius="lg"
                  h="48px"
                >
                  Form Fields
                </Button>
                <Button
                  variant={activeTab === 'patientContext' ? 'solid' : 'ghost'}
                  colorScheme={activeTab === 'patientContext' ? 'teal' : 'gray'}
                  justifyContent="start"
                  leftIcon={<InfoIcon />}
                  onClick={() => showOnlySpecificSection('patientContext')}
                  borderRadius="lg"
                  h="48px"
                >
                  Patient Context
                </Button>
                <Button
                  variant={activeTab === 'pageConfig' ? 'solid' : 'ghost'}
                  colorScheme={activeTab === 'pageConfig' ? 'blue' : 'gray'}
                  justifyContent="start"
                  leftIcon={<StarIcon />}
                  onClick={() => showOnlySpecificSection('pageConfig')}
                  borderRadius="lg"
                  h="48px"
                >
                  Page Configuration
                </Button>
                <Button
                  variant={activeTab === 'ruleEngine' ? 'solid' : 'ghost'}
                  colorScheme={activeTab === 'ruleEngine' ? 'blue' : 'gray'}
                  justifyContent="start"
                  leftIcon={<SettingsIcon />}
                  onClick={() => showOnlySpecificSection('ruleEngine')}
                  borderRadius="lg"
                  h="48px"
                >
                  Rule Engine
                </Button>
              </VStack>
            </VStack>

            <Divider />

            {/* Quick Actions */}
            <VStack spacing={2} align="stretch">
                             <Text fontSize="sm" fontWeight="bold" color="gray.600" textTransform="uppercase" letterSpacing="wide" fontFamily="Sora, sans-serif">
                 Quick Actions
               </Text>
              <VStack spacing={2} align="stretch">
                <Button
                  leftIcon={<AddIcon />}
                  colorScheme="blue"
                  variant="outline"
                  size="sm"
                  justifyContent="flex-start"
                  textAlign="left"
                  w="100%"
                  onClick={() => {
                    addTaskReference();
                    showOnlySpecificSection('taskRefs');
                  }}
                  borderRadius="lg"
                >
                  Add Task Reference
                </Button>
                <Button
                  leftIcon={<AddIcon />}
                  colorScheme="purple"
                  variant="outline"
                  size="sm"
                  justifyContent="flex-start"
                  textAlign="left"
                  w="100%"
                  onClick={() => {
                    addCategory();
                  }}
                  borderRadius="lg"
                >
                  Add Category
                </Button>
                <Button
                  leftIcon={<AddIcon />}
                  colorScheme="green"
                  variant="outline"
                  size="sm"
                  justifyContent="flex-start"
                  textAlign="left"
                  w="100%"
                  onClick={() => {
                    addField();
                    showOnlySpecificSection('fields');
                  }}
                  borderRadius="lg"
                >
                  Add Field
                </Button>
                <Button
                  leftIcon={<CopyIcon />}
                  colorScheme="purple"
                  variant="outline"
                  size="sm"
                  justifyContent="flex-start"
                  textAlign="left"
                  w="100%"
                  onClick={() => copyToClipboard(JSON.stringify(config?.workflowConfiguration, null, 2))}
                  borderRadius="lg"
                >
                  Export JSON
                </Button>
                <Button
                  leftIcon={<EditIcon />}
                  colorScheme="orange"
                  variant="outline"
                  size="sm"
                  justifyContent="flex-start"
                  textAlign="left"
                  w="100%"
                  onClick={() => {
                    showOnlySpecificSection('pageConfig');
                    startEditingPageConfig();
                  }}
                  borderRadius="lg"
                >
                  Edit Page Config
                </Button>
                <Button
                  leftIcon={<AddIcon />}
                  colorScheme="purple"
                  variant="outline"
                  size="sm"
                  justifyContent="flex-start"
                  textAlign="left"
                  w="100%"
                  onClick={() => {
                    setIsCreatingRule(true);
                    setEditingRule(null);
                    showOnlySpecificSection('ruleEngine');
                  }}
                  borderRadius="lg"
                >
                  Create Rule
                </Button>
              </VStack>
            </VStack>

            <Divider />

            {/* Configuration Status */}
            <VStack spacing={2} align="stretch">
                             <Text fontSize="sm" fontWeight="bold" color="gray.600" textTransform="uppercase" letterSpacing="wide" fontFamily="Sora, sans-serif">
                 Status
               </Text>
              <VStack spacing={3} align="stretch">
                <HStack justify="space-between" p={3} bg="blue.50" borderRadius="lg">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="medium" color="blue.700">Task References</Text>
                    <Text fontSize="xs" color="blue.600">{taskRefCount} active</Text>
                  </VStack>
                                     <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>{taskRefCount}</Badge>
                </HStack>
                <HStack justify="space-between" p={3} bg="purple.50" borderRadius="lg" cursor="pointer" onClick={() => showOnlySpecificSection('categories')} _hover={{ bg: "purple.100" }} transition="all 0.15s">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="medium" color="purple.700">Categories</Text>
                    <Text fontSize="xs" color="purple.600">{categoryCount} defined</Text>
                  </VStack>
                  <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>{categoryCount}</Badge>
                </HStack>
                <HStack justify="space-between" p={3} bg="green.50" borderRadius="lg">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="medium" color="green.700">Form Fields</Text>
                    <Text fontSize="xs" color="green.600">{fieldCount} available</Text>
                  </VStack>
                                     <Badge colorScheme="green" borderRadius="full" px={3} py={1}>{fieldCount}</Badge>
                </HStack>
                <HStack justify="space-between" p={3} bg="purple.50" borderRadius="lg">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="medium" color="purple.700">Page Config</Text>
                    <Text fontSize="xs" color="purple.600">Configured</Text>
                  </VStack>
                                     <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
                                      {config?.workflowConfiguration?.page?.title ? '✓' : '!'}
                                    </Badge>
                </HStack>
                <HStack justify="space-between" p={3} bg="indigo.50" borderRadius="lg">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="medium" color="indigo.700">Rules</Text>
                    <Text fontSize="xs" color="indigo.600">{rules.length} active</Text>
                  </VStack>
                  <Badge colorScheme="indigo" borderRadius="full" px={3} py={1}>{rules.length}</Badge>
                </HStack>
              </VStack>
            </VStack>
          </VStack>
        </Box>

        {/* Main Content Area */}
        <Box flex={1} p={6} w="full" maxW="100%" overflowX="hidden">
          {/* Statistics Dashboard - Hide when Rule Engine is active */}
          {activeTab !== 'ruleEngine' && showOnlySection !== 'ruleEngine' && (
            <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={6} mb={8}>
            <GridItem>
              <Card 
                bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" 
                color="white" 
                borderRadius="xl" 
                shadow="lg"
                _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
                transition="all 0.2s"
              >
                <CardBody>
                  <Stat>
                    <StatLabel fontSize="sm" opacity={0.9}>Task References</StatLabel>
                    <StatNumber fontSize="3xl" fontWeight="bold">{taskRefCount}</StatNumber>
                    <StatHelpText opacity={0.8}>Active configurations</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card 
                bg="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" 
                color="white" 
                borderRadius="xl" 
                shadow="lg"
                _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
                transition="all 0.2s"
              >
                <CardBody>
                  <Stat>
                    <StatLabel fontSize="sm" opacity={0.9}>Form Fields</StatLabel>
                    <StatNumber fontSize="3xl" fontWeight="bold">{fieldCount}</StatNumber>
                    <StatHelpText opacity={0.8}>Available fields</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card 
                bg="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" 
                color="white" 
                borderRadius="xl" 
                shadow="lg"
                _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
                transition="all 0.2s"
              >
                <CardBody>
                  <Stat>
                    <StatLabel fontSize="sm" opacity={0.9}>Config Size</StatLabel>
                    <StatNumber fontSize="3xl" fontWeight="bold">
                      {Math.round(JSON.stringify(config?.workflowConfiguration).length / 1024)}KB
                    </StatNumber>
                    <StatHelpText opacity={0.8}>Total configuration</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem>
              <Card 
                bg="linear-gradient(135deg, #fa709a 0%, #fee140 100%)" 
                color="white" 
                borderRadius="xl" 
                shadow="lg"
                _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
                transition="all 0.2s"
              >
                <CardBody>
                  <Stat>
                    <StatLabel fontSize="sm" opacity={0.9}>Last Updated</StatLabel>
                    <StatNumber fontSize="3xl" fontWeight="bold">Today</StatNumber>
                    <StatHelpText opacity={0.8}>Configuration status</StatHelpText>
                  </Stat>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>
          )}

          {/* Page Configuration Section */}
          {(isEditingPageConfig || activeSection === 'pageConfig' || showOnlySection === 'pageConfig') && (
                      <Card mb={6} bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="xl">
              <CardHeader pb={3}>
                <Flex justify="space-between" align="center">
                  <HStack>
                    <Box bg="purple.500" p={2} borderRadius="lg">
                      <SettingsIcon color="white" boxSize={5} />
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="lg" fontWeight="bold" color="purple.700" fontFamily="Sora, sans-serif">
                        Page Configuration
                      </Text>
                      <Text fontSize="sm" color="purple.600" fontFamily="Roboto, sans-serif">
                        Manage page title and description
                      </Text>
                    </VStack>
                  </HStack>
                  <HStack spacing={2}>
                    {showOnlySection === 'pageConfig' && (
                      <Button
                        leftIcon={<ViewIcon />}
                        colorScheme="purple"
                        variant="outline"
                        size="sm"
                        onClick={returnToOverview}
                        borderRadius="lg"
                      >
                        Back to Overview
                      </Button>
                    )}
                    {!isEditingPageConfig && (
                      <Button
                        leftIcon={<EditIcon />}
                        colorScheme="purple"
                        variant="outline"
                        size="sm"
                        onClick={startEditingPageConfig}
                        borderRadius="lg"
                      >
                        Edit
                      </Button>
                    )}
                  </HStack>
                </Flex>
              </CardHeader>
            <CardBody pt={0}>
              {!isEditingPageConfig ? (
                // Read-only view
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="purple.700" mb={2}>Page Title</Text>
                    <Text fontSize="md" color="gray.800" p={3} borderRadius="lg" border="1px solid" borderColor="purple.200">
                      {config?.workflowConfiguration?.page?.title || 'No title set'}
                    </Text>
                  </Box>
                  
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="purple.700" mb={2}>Page Description</Text>
                    <Text fontSize="md" color="gray.800" p={3} borderRadius="lg" border="1px solid" borderColor="purple.200" minH="80px">
                      {config?.workflowConfiguration?.page?.description || 'No description set'}
                    </Text>
                  </Box>
                  
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="purple.700" mb={2}>Show Logs</Text>
                    <Text fontSize="md" color="gray.800" p={3} borderRadius="lg" border="1px solid" borderColor="purple.200">
                      {config?.workflowConfiguration?.page?.showLogs ? 'Enabled' : 'Disabled'}
                    </Text>
                  </Box>
                </VStack>
              ) : (
                // Edit mode
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="medium" color="purple.700">Page Title</FormLabel>
                    <Input
                      value={pageConfigTemp.title}
                      onChange={(e) => setPageConfigTemp(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Enter page title"
                      bg="white"
                      border="1px solid"
                      borderColor="purple.300"
                      borderRadius="lg"
                      _focus={{ borderColor: "purple.500", boxShadow: "0 0 0 1px purple.500" }}
                    />
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="medium" color="purple.700">Page Description</FormLabel>
                    <Textarea
                      value={pageConfigTemp.description}
                      onChange={(e) => setPageConfigTemp(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter page description"
                      bg="white"
                      border="1px solid"
                      borderColor="purple.300"
                      borderRadius="lg"
                      _focus={{ borderColor: "purple.500", boxShadow: "0 0 0 1px purple.500" }}
                      rows={3}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="medium" color="purple.700">Show Logs</FormLabel>
                    <HStack spacing={4}>
                      <Button
                        size="sm"
                        variant={pageConfigTemp.showLogs ? "solid" : "outline"}
                        colorScheme="purple"
                        onClick={() => setPageConfigTemp(prev => ({ ...prev, showLogs: true }))}
                        borderRadius="lg"
                      >
                        Enabled
                      </Button>
                      <Button
                        size="sm"
                        variant={!pageConfigTemp.showLogs ? "solid" : "outline"}
                        colorScheme="gray"
                        onClick={() => setPageConfigTemp(prev => ({ ...prev, showLogs: false }))}
                        borderRadius="lg"
                      >
                        Disabled
                      </Button>
                    </HStack>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      When enabled, shows a "Display Logs" button in the Content Engine to view JSON data
                    </Text>
                  </FormControl>

                  {/* Save/Cancel buttons */}
                  <HStack spacing={3} justify="flex-end" pt={2}>
                    <Button
                      variant="outline"
                      colorScheme="gray"
                      size="sm"
                      onClick={cancelEditingPageConfig}
                      borderRadius="lg"
                    >
                      Cancel
                    </Button>
                    <Button
                      colorScheme="purple"
                      size="sm"
                      onClick={savePageConfig}
                      isLoading={isSaving}
                      loadingText="Saving..."
                      borderRadius="lg"
                    >
                      Save Changes
                    </Button>
                  </HStack>
                </VStack>
              )}
            </CardBody>
          </Card>
          )}

          {/* Patient Context Section */}
          {(activeSection === 'patientContext' || showOnlySection === 'patientContext') && (
            <Card mb={6} bg="teal.50" border="1px solid" borderColor="teal.200" borderRadius="xl">
              <CardHeader pb={3}>
                <Flex justify="space-between" align="center">
                  <HStack>
                    <Box bg="teal.500" p={2} borderRadius="lg">
                      <InfoIcon color="white" boxSize={5} />
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="lg" fontWeight="bold" color="teal.700" fontFamily="Sora, sans-serif">
                        Patient Context Editor
                      </Text>
                      <Text fontSize="sm" color="teal.600" fontFamily="Roboto, sans-serif">
                        Update patient demographics and per-program enrolled/selected state
                      </Text>
                    </VStack>
                  </HStack>
                  <HStack spacing={2}>
                    {showOnlySection === 'patientContext' && (
                      <Button
                        leftIcon={<ViewIcon />}
                        colorScheme="teal"
                        variant="outline"
                        size="sm"
                        onClick={returnToOverview}
                        borderRadius="lg"
                      >
                        Back to Overview
                      </Button>
                    )}
                  </HStack>
                </Flex>
              </CardHeader>
              <CardBody pt={0}>
                <VStack spacing={5} align="stretch">
                  <HStack spacing={3} align="end">
                    <FormControl maxW="360px">
                      <FormLabel fontSize="sm" fontWeight="medium" color="teal.700">Patient ID</FormLabel>
                      <Input
                        value={patientContextPatientId}
                        onChange={(e) => setPatientContextPatientId(e.target.value)}
                        placeholder="Enter patient ID"
                        bg="white"
                        border="1px solid"
                        borderColor="teal.300"
                        borderRadius="lg"
                        _focus={{ borderColor: 'teal.500', boxShadow: '0 0 0 1px teal.500' }}
                      />
                    </FormControl>
                    <Button
                      colorScheme="teal"
                      onClick={() => loadPatientContext(patientContextPatientId)}
                      isLoading={patientContextLoading}
                      loadingText="Loading..."
                      borderRadius="lg"
                    >
                      Load Context
                    </Button>
                    <Button
                      variant="outline"
                      colorScheme="teal"
                      onClick={savePatientContext}
                      isLoading={patientContextSaving}
                      loadingText="Saving..."
                      borderRadius="lg"
                      isDisabled={!String(patientContextPatientId || '').trim() || patientPrograms.length === 0}
                    >
                      Save Context
                    </Button>
                  </HStack>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" color="teal.700">First Name</FormLabel>
                      <Input value={patientContextForm.firstName} onChange={(e) => setPatientContextForm((prev) => ({ ...prev, firstName: e.target.value }))} bg="white" borderRadius="lg" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" color="teal.700">Last Name</FormLabel>
                      <Input value={patientContextForm.lastName} onChange={(e) => setPatientContextForm((prev) => ({ ...prev, lastName: e.target.value }))} bg="white" borderRadius="lg" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" color="teal.700">BirthDay</FormLabel>
                      <Input type="date" value={patientContextForm.birthDay} onChange={(e) => setPatientContextForm((prev) => ({ ...prev, birthDay: e.target.value }))} bg="white" borderRadius="lg" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" color="teal.700">Gender</FormLabel>
                      <Input value={patientContextForm.gender} onChange={(e) => setPatientContextForm((prev) => ({ ...prev, gender: e.target.value }))} bg="white" borderRadius="lg" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" color="teal.700">Phone Number</FormLabel>
                      <Input value={patientContextForm.phoneNumber} onChange={(e) => setPatientContextForm((prev) => ({ ...prev, phoneNumber: e.target.value }))} bg="white" borderRadius="lg" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" color="teal.700">Language</FormLabel>
                      <Input value={patientContextForm.language} onChange={(e) => setPatientContextForm((prev) => ({ ...prev, language: e.target.value }))} bg="white" borderRadius="lg" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" color="teal.700">Email</FormLabel>
                      <Input type="email" value={patientContextForm.email} onChange={(e) => setPatientContextForm((prev) => ({ ...prev, email: e.target.value }))} bg="white" borderRadius="lg" />
                    </FormControl>
                    <FormControl display="flex" alignItems="center" pt={8}>
                      <Checkbox
                        isChecked={patientContextForm.isTestAccount}
                        onChange={(e) => setPatientContextForm((prev) => ({ ...prev, isTestAccount: e.target.checked }))}
                        colorScheme="teal"
                      >
                        Is Test Account
                      </Checkbox>
                    </FormControl>
                  </SimpleGrid>

                  <Box>
                    <Text fontSize="md" fontWeight="semibold" color="teal.700" mb={3}>
                      Program Enrollment State
                    </Text>
                    <Text fontSize="xs" color="teal.600" mb={3}>
                      A program can be Enrolled, Selected, or neither. It cannot be both.
                    </Text>
                    <TableContainer bg="white" borderRadius="lg" border="1px solid" borderColor="teal.100">
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Program</Th>
                            <Th>Code</Th>
                            <Th textAlign="center">IsEnrolled</Th>
                            <Th textAlign="center">IsSelected</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {patientPrograms.map((program, index) => (
                            <Tr key={program.programId || `${program.programCode}-${index}`}>
                              <Td>
                                <VStack align="start" spacing={0}>
                                  <Text fontSize="sm" fontWeight="medium">{program.name}</Text>
                                  {program.description ? <Text fontSize="xs" color="gray.500">{program.description}</Text> : null}
                                </VStack>
                              </Td>
                              <Td>{program.programCode}</Td>
                              <Td textAlign="center">
                                <Checkbox
                                  isChecked={!!program.isEnrolled}
                                  onChange={(e) => setPatientProgramState(index, 'isEnrolled', e.target.checked)}
                                  colorScheme="teal"
                                />
                              </Td>
                              <Td textAlign="center">
                                <Checkbox
                                  isChecked={!!program.isSelected}
                                  onChange={(e) => setPatientProgramState(index, 'isSelected', e.target.checked)}
                                  colorScheme="purple"
                                />
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          )}

           {/* Search Results */}
           {searchResults && searchQuery && (
             <Card mb={6} bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="xl">
               <CardHeader pb={3}>
                 <Flex justify="space-between" align="center">
                   <HStack>
                     <SearchIcon color="blue.600" />
                     <Text fontSize="lg" fontWeight="bold" color="blue.700" fontFamily="Sora, sans-serif">
                       Search Results for "{searchQuery}"
                     </Text>
                   </HStack>
                   <Button size="sm" variant="ghost" onClick={clearSearch} leftIcon={<CloseIcon />}>
                     Clear Search
                   </Button>
                 </Flex>
               </CardHeader>
               <CardBody pt={0}>
                 <VStack spacing={4} align="stretch">
                   {/* Task Reference Results */}
                   {searchResults.taskReferences.length > 0 && (
                     <Box>
                       <Text fontSize="md" fontWeight="semibold" color="blue.700" mb={3}>
                         Task References ({searchResults.taskReferences.length})
                       </Text>
                       <VStack spacing={2} align="stretch">
                        {searchResults.taskReferences.map(({ categoryIndex, taskIndex, category, taskRef, matches }) => (
                          <Card key={`${categoryIndex}-${taskIndex}`} variant="outline" size="sm" bg="white">
                             <CardBody py={3}>
                               <VStack align="start" spacing={2}>
                                 <HStack justify="space-between" w="full">
                                   <Text fontWeight="bold" color="gray.800">{taskRef.label}</Text>
                                   <Button
                                     size="xs"
                                     colorScheme="blue"
                                     variant="outline"
                                                                          onClick={() => {
                                       setEditingTaskRef({ categoryIndex, taskIndex });
                                       showOnlySpecificSection('taskRefs');
                                       clearSearch();
                                     }}
                                   >
                                     Edit
                                   </Button>
                                 </HStack>
                                 <Text fontSize="sm" color="gray.600" fontFamily="mono">{taskRef.value}</Text>
                                 <VStack align="start" spacing={1}>
                                   {matches.map((match, matchIndex) => (
                                     <Badge key={matchIndex} colorScheme="blue" variant="subtle" size="sm">
                                       {match}
                                     </Badge>
                                   ))}
                                 </VStack>
                               </VStack>
                             </CardBody>
                           </Card>
                         ))}
                       </VStack>
                     </Box>
                   )}

                   {/* Field Results */}
                   {searchResults.fields.length > 0 && (
                     <Box>
                       <Text fontSize="md" fontWeight="semibold" color="green.700" mb={3}>
                         Form Fields ({searchResults.fields.length})
                       </Text>
                       <VStack spacing={2} align="stretch">
                         {searchResults.fields.map(({ fieldKey, field, matches }) => (
                           <Card key={fieldKey} variant="outline" size="sm" bg="white">
                             <CardBody py={3}>
                               <VStack align="start" spacing={2}>
                                 <HStack justify="space-between" w="full">
                                   <Text fontWeight="bold" color="gray.800">{field.label}</Text>
                                   <Button
                                     size="xs"
                                     colorScheme="green"
                                     variant="outline"
                                     onClick={() => {
                                       setEditingField(fieldKey);
                                       showOnlySpecificSection('fields');
                                       clearSearch();
                                     }}
                                   >
                                     Edit
                                   </Button>
                                 </HStack>
                                 <Text fontSize="sm" color="gray.600" fontFamily="mono">{fieldKey}</Text>
                                 <HStack spacing={2}>
                                   <Badge colorScheme="green" size="sm">{field.type}</Badge>
                                   {field.required && <Badge colorScheme="red" size="sm">Required</Badge>}
                                 </HStack>
                                 <VStack align="start" spacing={1}>
                                   {matches.map((match, matchIndex) => (
                                     <Badge key={matchIndex} colorScheme="green" variant="subtle" size="sm">
                                       {match}
                                     </Badge>
                                   ))}
                                 </VStack>
                               </VStack>
                             </CardBody>
                           </Card>
                         ))}
                       </VStack>
                     </Box>
                   )}

                   {/* No Results */}
                   {searchResults.taskReferences.length === 0 && searchResults.fields.length === 0 && (
                     <Box textAlign="center" py={6}>
                       <SearchIcon color="gray.400" boxSize={8} mb={3} />
                       <Text color="gray.500">No results found for "{searchQuery}"</Text>
                       <Text fontSize="sm" color="gray.400">Try searching for different terms</Text>
                     </Box>
                   )}
                 </VStack>
               </CardBody>
             </Card>
           )}

                        {/* Main Content Cards */}
              {(activeTab === 'overview' && !showOnlySection) && (
               <VStack spacing={8} align="stretch">
                                   {/* Welcome Section */}
                  <Card bg="linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)" borderRadius="xl" shadow="lg">
                    <CardBody>
                      <Flex justify="space-between" align="center">
                        <VStack align="start" spacing={2}>
                                                     <Text fontSize="2xl" fontWeight="bold" color="gray.800" fontFamily="Sora, sans-serif">Workflow Configuration Dashboard</Text>
                          <Text fontSize="lg" color="gray.600">Welcome to your Workflow Configuration Dashboard</Text>
                          <Text fontSize="sm" color="gray.500">Manage your task references and form fields with ease</Text>
                        </VStack>
                        <Box bg="white" p={4} borderRadius="xl" shadow="md">
                          <StarIcon color="orange.500" boxSize={8} />
                        </Box>
                      </Flex>
                    </CardBody>
                  </Card>

                 

                 {/* Quick Actions */}
                 <Box>
                                       <Heading size="lg" color="gray.800" mb={6} fontFamily="Sora, sans-serif">Quick Actions</Heading>
                   <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={6}>
                                           <Card 
                        bg="rgba(255, 255, 255, 0.8)" 
                        backdropFilter="blur(10px)"
                        borderRadius="xl" 
                        shadow="lg" 
                        border="1px solid"
                        borderColor="rgba(255, 255, 255, 0.2)"
                        _hover={{ 
                          transform: "translateY(-8px) scale(1.02)", 
                          shadow: "xl",
                          bg: "rgba(255, 255, 255, 0.9)"
                        }}
                        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                        cursor="pointer"
                        onClick={() => {
                          addTaskReference();
                          showOnlySpecificSection('taskRefs');
                        }}
                      >
                       <CardBody textAlign="center" py={8}>
                         <Box bg="blue.100" p={4} borderRadius="xl" mx="auto" mb={4} w="fit-content">
                           <AddIcon color="blue.600" boxSize={8} />
                         </Box>
                                                   <Text fontSize="xl" fontWeight="bold" color="blue.700" mb={2} fontFamily="Sora, sans-serif">Add Task Reference</Text>
                         <Text fontSize="sm" color="blue.600">Create new task reference configuration</Text>
                       </CardBody>
                     </Card>

                                                 <Card 
                                                    bg="rgba(255, 255, 255, 0.8)" 
                                                    backdropFilter="blur(10px)"
                                                    borderRadius="xl" 
                                                    shadow="lg" 
                                                    border="1px solid"
                                                    borderColor="rgba(255, 255, 255, 0.2)"
                                                    _hover={{ 
                                                      transform: "translateY(-8px) scale(1.02)", 
                                                      shadow: "xl",
                                                      bg: "rgba(255, 255, 255, 0.9)"
                                                    }}
                                                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                                    cursor="pointer"
                                                    onClick={() => {
                                                      addCategory();
                                                      showOnlySpecificSection('categories');
                                                    }}
                                                  >
                                                   <CardBody textAlign="center" py={8}>
                                                     <Box bg="purple.100" p={4} borderRadius="xl" mx="auto" mb={4} w="fit-content">
                                                       <AddIcon color="purple.600" boxSize={8} />
                                                     </Box>
                                                     <Text fontSize="xl" fontWeight="bold" color="purple.700" mb={2} fontFamily="Sora, sans-serif">Add Category</Text>
                                                     <Text fontSize="sm" color="purple.600">Create a category for organizing task references</Text>
                                                   </CardBody>
                                                 </Card>
                     
                                           <Card 
                        bg="rgba(255, 255, 255, 0.8)" 
                        backdropFilter="blur(10px)"
                        borderRadius="xl" 
                        shadow="lg" 
                        border="1px solid"
                        borderColor="rgba(255, 255, 255, 0.2)"
                        _hover={{ 
                          transform: "translateY(-8px) scale(1.02)", 
                          shadow: "xl",
                          bg: "rgba(255, 255, 255, 0.9)"
                        }}
                        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                        cursor="pointer"
                        onClick={() => {
                          addField();
                          showOnlySpecificSection('fields');
                        }}
                      >
                       <CardBody textAlign="center" py={8}>
                         <Box bg="green.100" p={4} borderRadius="xl" mx="auto" mb={4} w="fit-content">
                           <AddIcon color="green.600" boxSize={8} />
                         </Box>
                                                   <Text fontSize="xl" fontWeight="bold" color="green.700" mb={2} fontFamily="Sora, sans-serif">Add Form Field</Text>
                         <Text fontSize="sm" color="green.600">Create new form field configuration</Text>
                       </CardBody>
                     </Card>

                       <Card 
                          bg="rgba(255, 255, 255, 0.8)" 
                          backdropFilter="blur(10px)"
                          borderRadius="xl" 
                          shadow="lg" 
                          border="1px solid"
                          borderColor="rgba(255, 255, 255, 0.2)"
                          _hover={{ 
                            transform: "translateY(-8px) scale(1.02)", 
                            shadow: "xl",
                            bg: "rgba(255, 255, 255, 0.9)"
                          }}
                          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                          cursor="pointer"
                          onClick={() => showOnlySpecificSection('patientContext')}
                        >
                         <CardBody textAlign="center" py={8}>
                           <Box bg="teal.100" p={4} borderRadius="xl" mx="auto" mb={4} w="fit-content">
                             <InfoIcon color="teal.600" boxSize={8} />
                           </Box>
                           <Text fontSize="xl" fontWeight="bold" color="teal.700" mb={2} fontFamily="Sora, sans-serif">Patient Context</Text>
                           <Text fontSize="sm" color="teal.600">Update patient demographic and program flags</Text>
                         </CardBody>
                       </Card>
                     
                                           <Card 
                        bg="rgba(255, 255, 255, 0.8)" 
                        backdropFilter="blur(10px)"
                        borderRadius="xl" 
                        shadow="lg" 
                        border="1px solid"
                        borderColor="rgba(255, 255, 255, 0.2)"
                        _hover={{ 
                          transform: "translateY(-8px) scale(1.02)", 
                          shadow: "xl",
                          bg: "rgba(255, 255, 255, 0.9)"
                        }}
                        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                        cursor="pointer"
                        onClick={() => copyToClipboard(JSON.stringify(config?.workflowConfiguration, null, 2))}
                      >
                       <CardBody textAlign="center" py={8}>
                         <Box bg="purple.100" p={4} borderRadius="xl" mx="auto" mb={4} w="fit-content">
                           <CopyIcon color="purple.600" boxSize={8} />
                         </Box>
                                                   <Text fontSize="xl" fontWeight="bold" color="purple.700" mb={2} fontFamily="Sora, sans-serif">Export JSON</Text>
                         <Text fontSize="sm" color="purple.600">Copy configuration to clipboard</Text>
                       </CardBody>
                     </Card>
                     
                     <Card 
                        bg="rgba(255, 255, 255, 0.8)" 
                        backdropFilter="blur(10px)"
                        borderRadius="xl" 
                        shadow="lg" 
                        border="1px solid"
                        borderColor="rgba(255, 255, 255, 0.2)"
                        _hover={{ 
                          transform: "translateY(-8px) scale(1.02)", 
                          shadow: "xl",
                          bg: "rgba(255, 255, 255, 0.9)"
                        }}
                        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                        cursor="pointer"
                        onClick={() => {
                          showOnlySpecificSection('pageConfig');
                        }}
                      >
                       <CardBody textAlign="center" py={8}>
                         <Box bg="purple.100" p={4} borderRadius="xl" mx="auto" mb={4} w="fit-content">
                           <StarIcon color="purple.600" boxSize={8} />
                         </Box>
                                                   <Text fontSize="xl" fontWeight="bold" color="purple.700" mb={2} fontFamily="Sora, sans-serif">Page Configuration</Text>
                         <Text fontSize="sm" color="purple.600">Configure page settings and layout</Text>
                       </CardBody>
                     </Card>
                     
                   </SimpleGrid>
                 </Box>

                 {/* Recent Configuration */}
                 <Box>
                                       <Heading size="lg" color="gray.800" mb={6} fontFamily="Sora, sans-serif">Recent Configuration</Heading>
                   <Card bg="white" borderRadius="xl" shadow="lg">
                     <CardBody>
                       <VStack align="stretch" spacing={4}>
                      {config?.workflowConfiguration?.taskReference?.categories?.slice(0, 2).flatMap((category, categoryIndex) =>
                        category.options?.slice(0, 2).map((taskRef, taskIndex) => (
                          <HStack key={`${categoryIndex}-${taskIndex}`} justify="space-between" p={4} bg="gray.50" borderRadius="lg" _hover={{ bg: "gray.100" }} transition="all 0.2s">
                             <VStack align="start" spacing={1}>
                               <Text fontWeight="bold" color="gray.800">{taskRef.label}</Text>
                               <Text fontSize="sm" color="gray.500" fontFamily="mono">{taskRef.value}</Text>
                              <Badge colorScheme="blue" size="sm">{category.name}</Badge>
                             </VStack>
                             <Button
                               size="sm"
                               colorScheme="blue"
                               variant="outline"
                               onClick={() => {
                                setEditingTaskRef({ categoryIndex, taskIndex });
                                 showOnlySpecificSection('taskRefs');
                               }}
                               borderRadius="lg"
                             >
                               Edit
                             </Button>
                           </HStack>
                        ))
                      )}
                         {availableFields.slice(0, 3).map(fieldKey => {
                           const field = config.workflowConfiguration[fieldKey];
                           return (
                             <HStack key={fieldKey} justify="space-between" p={4} bg="gray.50" borderRadius="lg" _hover={{ bg: "gray.100" }} transition="all 0.2s">
                               <VStack align="start" spacing={1}>
                                 <Text fontWeight="bold" color="gray.800">{field.label}</Text>
                                 <Text fontSize="sm" color="gray.500" fontFamily="mono">{fieldKey}</Text>
                               </VStack>
                               <Button
                                 size="sm"
                                 colorScheme="green"
                                 variant="outline"
                                 onClick={() => {
                                   setEditingField(fieldKey);
                                   showOnlySpecificSection('fields');
                                 }}
                                 borderRadius="lg"
                               >
                                 Edit
                               </Button>
                             </HStack>
                           );
                         })}
                         
                       </VStack>
                     </CardBody>
                   </Card>
                 </Box>
               </VStack>
             )}


                                          {(activeTab === 'taskRefs' || showOnlySection === 'taskRefs' || activeTab === 'categories' || showOnlySection === 'categories') && (
               <VStack spacing={6} align="stretch">
                 {showOnlySection === 'taskRefs' && (
                   <Flex justify="space-between" align="center">
                     <Heading size="lg" color="gray.800" fontFamily="Sora, sans-serif">Task References</Heading>
                     <HStack spacing={3}>
                       <Button
                         colorScheme="purple"
                         variant="outline"
                         size="sm"
                         onClick={() => showOnlySpecificSection('categories')}
                         borderRadius="lg"
                       >
                         Manage Categories
                       </Button>
                       <Button
                         leftIcon={<ViewIcon />}
                         colorScheme="blue"
                         variant="outline"
                         size="sm"
                         onClick={returnToOverview}
                         borderRadius="lg"
                       >
                         Back to Overview
                       </Button>
                     </HStack>
                   </Flex>
                 )}
              {/* ── Categories Section ── */}
              {(activeTab === 'categories' || showOnlySection === 'categories') && (
                <VStack spacing={6} align="stretch">
                  {showOnlySection === 'categories' && (
                    <Flex justify="space-between" align="center">
                      <Heading size="lg" color="gray.800" fontFamily="Sora, sans-serif">Categories</Heading>
                      <Button leftIcon={<ViewIcon />} colorScheme="purple" variant="outline" size="sm" onClick={returnToOverview} borderRadius="lg">
                        Back to Overview
                      </Button>
                    </Flex>
                  )}

                  {addingNewItem.type === 'category' ? (
                    <VStack spacing={6} align="stretch">
                      <Flex justify="space-between" align="center">
                        <Heading size="lg" color="gray.800" fontFamily="Sora, sans-serif">Add New Category</Heading>
                        <Button leftIcon={<CloseIcon />} variant="ghost" onClick={cancelNewCategory} borderRadius="lg">Cancel</Button>
                      </Flex>
                      <Card bg="rgba(255,255,255,0.8)" backdropFilter="blur(10px)" borderRadius="xl" shadow="lg" border="1px solid" borderColor="rgba(255,255,255,0.2)" _hover={{ transform: "translateY(-4px)", shadow: "xl", bg: "rgba(255,255,255,0.9)" }} transition="all 0.3s cubic-bezier(0.4,0,0.2,1)">
                        <CardHeader><Heading size="md" color="purple.700" fontFamily="Sora, sans-serif">New Category Details</Heading></CardHeader>
                        <CardBody>
                          <VStack spacing={4} align="stretch">
                            <SimpleGrid columns={2} spacing={4}>
                              <FormControl>
                                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Category Name</FormLabel>
                                <Input value={addingNewItem.data?.name || ""} onChange={(e) => setAddingNewItem(prev => ({ ...prev, data: { ...prev.data, name: e.target.value } }))} placeholder="e.g., Heart Management and Care" size="sm" borderRadius="lg" border="1px solid" borderColor="gray.300" />
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Icon</FormLabel>
                                <IconSelector value={addingNewItem.data?.icon || ""} onChange={(value) => setAddingNewItem(prev => ({ ...prev, data: { ...prev.data, icon: value } }))} placeholder="Search for an icon..." />
                              </FormControl>
                            </SimpleGrid>
                            <FormControl>
                              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Description</FormLabel>
                              <Textarea value={addingNewItem.data?.description || ""} onChange={(e) => setAddingNewItem(prev => ({ ...prev, data: { ...prev.data, description: e.target.value } }))} placeholder="Describe this category..." size="sm" borderRadius="lg" border="1px solid" borderColor="gray.300" rows={3} />
                            </FormControl>
                            <Flex justify="flex-end" mt={2}>
                              <Button size="md" variant="outline" onClick={cancelNewCategory} borderRadius="lg" mr={3}>Cancel</Button>
                              <Button size="md" colorScheme="purple" onClick={saveNewCategory} isLoading={isSaving} loadingText="Saving..." borderRadius="lg">Save Category</Button>
                            </Flex>
                          </VStack>
                        </CardBody>
                      </Card>
                    </VStack>
                  ) : editingCategory !== null && categories[editingCategory] ? (
                    <VStack spacing={6} align="stretch">
                      <Flex justify="space-between" align="center">
                        <HStack spacing={3}>
                          {categories[editingCategory].icon && FiIcons[`Fi${categories[editingCategory].icon}`] && (
                            <Box p={2} borderRadius="lg" bgGradient="linear(135deg, purple.500, purple.700)">
                              {React.createElement(FiIcons[`Fi${categories[editingCategory].icon}`], { size: 20, color: 'white' })}
                            </Box>
                          )}
                          <Heading size="lg" color="gray.800" fontFamily="Sora, sans-serif">Edit Category</Heading>
                        </HStack>
                        <Button leftIcon={<CloseIcon />} variant="ghost" onClick={() => setEditingCategory(null)} borderRadius="lg">Cancel</Button>
                      </Flex>
                      <Card bg="rgba(255,255,255,0.8)" backdropFilter="blur(10px)" borderRadius="xl" shadow="lg" border="1px solid" borderColor="rgba(255,255,255,0.2)">
                        <CardHeader><Heading size="md" color="purple.700" fontFamily="Sora, sans-serif">{categories[editingCategory].name}</Heading></CardHeader>
                        <CardBody>
                          <VStack spacing={4} align="stretch">
                            <SimpleGrid columns={2} spacing={4}>
                              <FormControl>
                                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Category Name</FormLabel>
                                <Input value={categories[editingCategory].name || ""} onChange={(e) => updateCategory(editingCategory, 'name', e.target.value)} size="sm" borderRadius="lg" border="1px solid" borderColor="gray.300" />
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Icon</FormLabel>
                                <IconSelector value={categories[editingCategory].icon || ""} onChange={(val) => updateCategory(editingCategory, 'icon', val)} placeholder="Search for an icon..." />
                              </FormControl>
                            </SimpleGrid>
                            <FormControl>
                              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Description</FormLabel>
                              <Textarea value={categories[editingCategory].description || ""} onChange={(e) => updateCategory(editingCategory, 'description', e.target.value)} size="sm" borderRadius="lg" border="1px solid" borderColor="gray.300" rows={3} />
                            </FormControl>
                            <Flex justify="flex-end" mt={2}>
                              <Button size="md" variant="outline" onClick={() => setEditingCategory(null)} borderRadius="lg" mr={3}>Cancel</Button>
                              <Button size="md" colorScheme="purple" onClick={async () => { await saveToDatabase(); setEditingCategory(null); }} isLoading={isSaving} loadingText="Saving..." borderRadius="lg">Save Changes</Button>
                            </Flex>
                          </VStack>
                        </CardBody>
                      </Card>
                    </VStack>
                  ) : (
                    <VStack spacing={6} align="stretch">
                      <Flex justify="space-between" align="center">
                        <HStack spacing={3}>
                          <Heading size="lg" color="gray.800" fontFamily="Sora, sans-serif">Categories</Heading>
                          <Badge colorScheme="purple" borderRadius="full" fontSize="md" px={3}>{categoryCount}</Badge>
                        </HStack>
                        <Button leftIcon={<AddIcon />} colorScheme="purple" size="md" onClick={addCategory} borderRadius="lg">
                          Add Category
                        </Button>
                      </Flex>

                      {categories.length === 0 ? (
                        <Card bg="white" borderRadius="xl" shadow="md" border="2px dashed" borderColor="purple.200">
                          <CardBody textAlign="center" py={16}>
                            <Box p={4} borderRadius="full" bg="purple.50" w="fit-content" mx="auto" mb={4}>
                              <AddIcon color="purple.400" boxSize={8} />
                            </Box>
                            <Text fontSize="lg" fontWeight="600" color="gray.700" mb={2}>No categories yet</Text>
                            <Text fontSize="sm" color="gray.500" mb={6} maxW="360px" mx="auto">
                              Categories group your task references. Create your first one — for example, "Heart Management and Care (HMC)".
                            </Text>
                            <Button colorScheme="purple" leftIcon={<AddIcon />} onClick={addCategory} borderRadius="lg">
                              Add First Category
                            </Button>
                          </CardBody>
                        </Card>
                      ) : (
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                          {categories.map((category, index) => {
                            const CatIcon = category.icon && FiIcons[`Fi${category.icon}`]
                              ? FiIcons[`Fi${category.icon}`]
                              : FiIcons.FiFolder;
                            const taskCount = category.options?.length || 0;
                            return (
                              <Card
                                key={index}
                                bg="rgba(255,255,255,0.95)"
                                borderRadius="xl"
                                shadow="md"
                                border="1px solid"
                                borderColor="rgba(139,92,246,0.12)"
                                _hover={{ transform: "translateY(-6px)", shadow: "xl", borderColor: "rgba(139,92,246,0.35)" }}
                                transition="all 0.25s cubic-bezier(0.4,0,0.2,1)"
                              >
                                <CardBody p={5}>
                                  <VStack spacing={4} align="stretch">
                                    <HStack spacing={4} align="start">
                                      <Flex
                                        w="56px"
                                        h="56px"
                                        borderRadius="full"
                                        flexShrink={0}
                                        align="center"
                                        justify="center"
                                        bg="purple.50"
                                        border="1px solid"
                                        borderColor="purple.200"
                                        color="purple.600"
                                      >
                                        {React.createElement(CatIcon, { size: 22 })}
                                      </Flex>
                                      <Box flex={1} minW={0}>
                                        <Text fontWeight="700" color="gray.800" fontSize="md" noOfLines={1}>{category.name}</Text>
                                        <Text fontSize="xs" color="gray.500" mt={0.5} noOfLines={2}>{category.description || "No description"}</Text>
                                      </Box>
                                    </HStack>

                                    <HStack spacing={2}>
                                      <Badge colorScheme={taskCount > 0 ? "purple" : "gray"} variant="subtle" borderRadius="full" fontSize="xs" px={3} py={1}>
                                        {taskCount} task{taskCount !== 1 ? 's' : ''}
                                      </Badge>
                                      {category.icon && (
                                        <Badge colorScheme="gray" variant="outline" borderRadius="full" fontSize="xs" px={2}>
                                          {category.icon}
                                        </Badge>
                                      )}
                                    </HStack>

                                    <HStack spacing={2} pt={2} borderTop="1px solid" borderColor="gray.100">
                                      <Button size="sm" flex={1} colorScheme="purple" variant="outline" leftIcon={<EditIcon />} onClick={() => { setEditingCategory(index); }} borderRadius="lg">Edit</Button>
                                      <Button size="sm" flex={1} colorScheme="blue" variant="ghost" fontSize="xs" onClick={() => showOnlySpecificSection('taskRefs')} borderRadius="lg">View Tasks</Button>
                                      <IconButton size="sm" icon={<DeleteIcon />} colorScheme="red" variant="ghost" borderRadius="lg" onClick={() => removeCategory(index)} aria-label="Delete" />
                                    </HStack>
                                  </VStack>
                                </CardBody>
                              </Card>
                            );
                          })}
                        </SimpleGrid>
                      )}
                    </VStack>
                  )}
                </VStack>
              )}

              {(activeTab === 'taskRefs' || showOnlySection === 'taskRefs') && (addingNewItem.type === 'category' ? (
                // New Category Form
                <VStack spacing={6} align="stretch">
                  <Flex justify="space-between" align="center">
                    <Heading size="lg" color="gray.800" fontFamily="Sora, sans-serif">Add New Category</Heading>
                    <Button
                      leftIcon={<CloseIcon />}
                      variant="ghost"
                      onClick={cancelNewCategory}
                      borderRadius="lg"
                    >
                      Cancel
                    </Button>
                  </Flex>

                  <Card
                    bg="rgba(255, 255, 255, 0.8)"
                    backdropFilter="blur(10px)"
                    borderRadius="xl"
                    shadow="lg"
                    border="1px solid"
                    borderColor="rgba(255, 255, 255, 0.2)"
                    _hover={{
                      transform: "translateY(-4px)",
                      shadow: "xl",
                      bg: "rgba(255, 255, 255, 0.9)"
                    }}
                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  >
                    <CardHeader>
                      <Heading size="md" color="purple.700" fontFamily="Sora, sans-serif">New Category Details</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        <SimpleGrid columns={2} spacing={4}>
                          <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Category Name</FormLabel>
                            <Input
                              value={addingNewItem.data?.name || ""}
                              onChange={(e) => setAddingNewItem(prev => ({ ...prev, data: { ...prev.data, name: e.target.value } }))}
                              placeholder="Enter category name"
                              size="sm"
                              borderRadius="lg"
                              border="1px solid"
                              borderColor="gray.300"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Icon</FormLabel>
                            <IconSelector
                              value={addingNewItem.data?.icon || ""}
                              onChange={(value) => setAddingNewItem(prev => ({ ...prev, data: { ...prev.data, icon: value } }))}
                              placeholder="Search for an icon..."
                            />
                          </FormControl>
                        </SimpleGrid>

                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Description</FormLabel>
                          <Textarea
                            value={addingNewItem.data?.description || ""}
                            onChange={(e) => setAddingNewItem(prev => ({ ...prev, data: { ...prev.data, description: e.target.value } }))}
                            placeholder="Enter category description"
                            size="sm"
                            borderRadius="lg"
                            border="1px solid"
                            borderColor="gray.300"
                            rows={3}
                          />
                        </FormControl>

                        <Flex justify="flex-end" spacing={3} mt={4}>
                          <Button
                            size="md"
                            variant="outline"
                            onClick={cancelNewCategory}
                            borderRadius="lg"
                            mr={3}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="md"
                            colorScheme="purple"
                            onClick={saveNewCategory}
                            isLoading={isSaving}
                            loadingText="Saving..."
                            borderRadius="lg"
                          >
                            Save New Category
                          </Button>
                        </Flex>
                      </VStack>
                    </CardBody>
                  </Card>
                </VStack>
              ) : addingNewItem.type === 'taskRef' ? (
                   // New Task Reference Form
                   <VStack spacing={6} align="stretch">
                     <Flex justify="space-between" align="center">
                                               <Heading size="lg" color="gray.800" fontFamily="Sora, sans-serif">Add New Task Reference</Heading>
                       <Button
                         leftIcon={<CloseIcon />}
                         variant="ghost"
                         onClick={cancelNewTaskReference}
                         borderRadius="lg"
                       >
                         Cancel
                       </Button>
                     </Flex>
                     
                     <Card 
                       bg="rgba(255, 255, 255, 0.8)" 
                       backdropFilter="blur(10px)"
                       borderRadius="xl" 
                       shadow="lg" 
                       border="1px solid"
                       borderColor="rgba(255, 255, 255, 0.2)"
                       _hover={{ 
                         transform: "translateY(-4px)", 
                         shadow: "xl",
                         bg: "rgba(255, 255, 255, 0.9)"
                       }}
                       transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                     >
                       <CardHeader>
                                                   <Heading size="md" color="blue.700" fontFamily="Sora, sans-serif">New Task Reference Details</Heading>
                       </CardHeader>
                       <CardBody>
                         <VStack spacing={4} align="stretch">
                           <SimpleGrid columns={2} spacing={4}>
                             <FormControl>
                               <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Label</FormLabel>
                               <Input
                                 value={addingNewItem.data?.label || ""}
                                 onChange={(e) => updateNewTaskRef('label', e.target.value)}
                                 placeholder="Enter task reference label"
                                 size="sm"
                                 borderRadius="lg"
                                 border="1px solid"
                                 borderColor="gray.300"
                               />
                             </FormControl>
                             
                             <FormControl>
                               <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Value</FormLabel>
                               <Input
                                 value={addingNewItem.data?.value || ""}
                                 onChange={(e) => updateNewTaskRef('value', e.target.value)}
                                 placeholder="Enter task reference value"
                                 size="sm"
                                 borderRadius="lg"
                                 border="1px solid"
                                 borderColor="gray.300"
                               />
                             </FormControl>
                           </SimpleGrid>

                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Category</FormLabel>
                          {config?.workflowConfiguration?.taskReference?.categories?.length ? (
                            <CategorySelectDropdown
                              categories={config.workflowConfiguration.taskReference.categories}
                              value={addingNewItem.data?.categoryIndex ?? ''}
                              onChange={(index) => updateNewTaskRef('categoryIndex', index)}
                            />
                          ) : (
                            <Box p={3} border="1px solid" borderColor="orange.200" borderRadius="lg" bg="orange.50">
                              <Text fontSize="sm" color="orange.600">No categories yet — add a category first.</Text>
                            </Box>
                          )}
                          {config?.workflowConfiguration?.taskReference?.categories?.length > 0 && (
                            <FormHelperText fontSize="xs">Select which category this task reference belongs to</FormHelperText>
                          )}
                        </FormControl>

                           <Divider />

                           <Box>
                             <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.700">Task Reference Fields</Text>
                             <Text fontSize="xs" color="gray.600" mb={3}>
                               Visible fields appear on the Content Engine form. Required fields must be filled before continuing.
                             </Text>
                             <VStack spacing={3} align="stretch">
                               {availableFields.map(fieldKey => {
                                 const isVisible = getTaskRefVisibleFields(addingNewItem.data).includes(fieldKey);
                                 const isRequired = getTaskRefRequiredFields(addingNewItem.data).includes(fieldKey);

                                 return (
                                   <Box key={fieldKey} p={3} borderWidth="1px" borderColor="gray.200" borderRadius="lg" bg="gray.50">
                                     <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={3}>
                                       {getFieldDisplayLabel(fieldKey)}
                                     </Text>
                                     <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                       <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                         <FormLabel fontSize="xs" color="gray.600" mb={0}>Visible</FormLabel>
                                         <Switch
                                           isChecked={isVisible}
                                           onChange={(e) => updateNewTaskRefVisibleFields(fieldKey, e.target.checked)}
                                           colorScheme="blue"
                                           size="lg"
                                         />
                                       </FormControl>
                                       <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                         <FormLabel fontSize="xs" color="gray.600" mb={0}>Required</FormLabel>
                                         <Switch
                                           isChecked={isRequired}
                                           onChange={(e) => updateNewTaskRefRequiredFields(fieldKey, e.target.checked)}
                                           colorScheme="green"
                                           size="lg"
                                           isDisabled={!isVisible}
                                         />
                                       </FormControl>
                                     </SimpleGrid>
                                   </Box>
                                 );
                               })}
                             </VStack>
                           </Box>

                                                       <Flex justify="flex-end" spacing={3} mt={4}>
                              <Button
                                size="md"
                                variant="outline"
                                onClick={cancelNewTaskReference}
                                borderRadius="lg"
                                mr={3}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="md"
                                colorScheme="blue"
                                onClick={saveNewTaskReference}
                                isLoading={isSaving}
                                loadingText="Saving..."
                                borderRadius="lg"
                            isDisabled={!addingNewItem.data?.categoryIndex && addingNewItem.data?.categoryIndex !== 0}
                              >
                                Save New Task Reference
                              </Button>
                            </Flex>
                         </VStack>
                       </CardBody>
                     </Card>
                   </VStack>
                 ) : (
                   // Existing Task References List
                   <>
                     {showOnlySection !== 'taskRefs' && (
                       <Flex justify="space-between" align="center">
                         <Heading size="lg" color="gray.800" fontFamily="Sora, sans-serif">Task References</Heading>
                         <HStack spacing={3}>
                           <Button
                             leftIcon={<AddIcon />}
                             colorScheme="purple"
                             size="md"
                             onClick={addCategory}
                             borderRadius="lg"
                           >
                             Add Category
                           </Button>
                           <Button
                             leftIcon={<AddIcon />}
                             colorScheme="blue"
                             size="md"
                             onClick={addTaskReference}
                             borderRadius="lg"
                             isDisabled={!config?.workflowConfiguration?.taskReference?.categories?.length}
                             title={!config?.workflowConfiguration?.taskReference?.categories?.length ? "Add a category first" : ""}
                           >
                             Add Task Reference
                           </Button>
                         </HStack>
                       </Flex>
                     )}
                     
                                                                                       {editingTaskRef !== null ? (
                           // Show only the editing form - Full width
                           <Card 
                      key={`${editingTaskRef.categoryIndex}-${editingTaskRef.taskIndex}`}
                             bg="rgba(255, 255, 255, 0.8)" 
                             backdropFilter="blur(10px)"
                             borderRadius="xl" 
                             shadow="lg" 
                             border="1px solid"
                             borderColor="rgba(255, 255, 255, 0.2)"
                             _hover={{ 
                               transform: "translateY(-8px) scale(1.02)", 
                               shadow: "xl",
                               bg: "rgba(255, 255, 255, 0.9)"
                             }}
                             transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                             w="full"
                           >
                            <CardHeader pb={3}>
                              <VStack align="start" spacing={3}>
                                <HStack justify="space-between" w="full">
                            <Heading size="md" color="blue.700">{config.workflowConfiguration.taskReference.categories[editingTaskRef.categoryIndex].options[editingTaskRef.taskIndex].label}</Heading>
                            <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>Task {editingTaskRef.taskIndex + 1}</Badge>
                                </HStack>
                          <HStack spacing={2} align="center">
                            <Text fontSize="sm" color="gray.600" fontWeight="medium">Category:</Text>
                            <Badge colorScheme="purple" size="sm" borderRadius="full">
                              {config.workflowConfiguration.taskReference.categories[editingTaskRef.categoryIndex].name}
                            </Badge>
                            {config.workflowConfiguration.taskReference.categories[editingTaskRef.categoryIndex].icon && (
                              <Text fontSize="sm" color="gray.500">
                                {config.workflowConfiguration.taskReference.categories[editingTaskRef.categoryIndex].icon}
                              </Text>
                            )}
                          </HStack>
                          <Text color="gray.500" fontFamily="mono" fontSize="sm" bg="gray.50" p={2} borderRadius="md">{config.workflowConfiguration.taskReference.categories[editingTaskRef.categoryIndex].options[editingTaskRef.taskIndex].value}</Text>
                                <HStack spacing={1} flexWrap="wrap">
                                  {getTaskRefVisibleFields(config.workflowConfiguration.taskReference.categories[editingTaskRef.categoryIndex].options[editingTaskRef.taskIndex]).map(fieldName => (
                                    <Badge
                                      key={fieldName}
                                      colorScheme={getTaskRefRequiredFields(config.workflowConfiguration.taskReference.categories[editingTaskRef.categoryIndex].options[editingTaskRef.taskIndex]).includes(fieldName) ? 'green' : 'blue'}
                                      size="sm"
                                      borderRadius="full"
                                    >
                                      {getFieldDisplayLabel(fieldName)} {getTaskRefRequiredFields(config.workflowConfiguration.taskReference.categories[editingTaskRef.categoryIndex].options[editingTaskRef.taskIndex]).includes(fieldName) ? '(Required)' : '(Visible)'}
                                    </Badge>
                                  ))}
                                </HStack>
                              </VStack>
                            </CardHeader>

                            <CardBody pt={0} pb={4}>
                              <VStack spacing={4} align="stretch">
                                <SimpleGrid columns={2} spacing={4}>
                                  <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Label</FormLabel>
                                    <Input
                                value={config.workflowConfiguration.taskReference.categories[editingTaskRef.categoryIndex].options[editingTaskRef.taskIndex].label}
                                onChange={(e) => updateTaskReference(editingTaskRef.categoryIndex, editingTaskRef.taskIndex, 'label', e.target.value)}
                                      placeholder="Enter task reference label"
                                      size="sm"
                                      borderRadius="lg"
                                      border="1px solid"
                                      borderColor="gray.300"
                                    />
                                  </FormControl>
                                  
                                  <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Value</FormLabel>
                                    <Input
                                value={config.workflowConfiguration.taskReference.categories[editingTaskRef.categoryIndex].options[editingTaskRef.taskIndex].value}
                                onChange={(e) => updateTaskReference(editingTaskRef.categoryIndex, editingTaskRef.taskIndex, 'value', e.target.value)}
                                      placeholder="Enter task reference value"
                                      size="sm"
                                      borderRadius="lg"
                                      border="1px solid"
                                      borderColor="gray.300"
                                    />
                                  </FormControl>
                                </SimpleGrid>

                                <FormControl>
                                  <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Category</FormLabel>
                                  <CategorySelectDropdown
                                    categories={config?.workflowConfiguration?.taskReference?.categories || []}
                                    value={editingTaskRef.categoryIndex}
                                    onChange={(newCategoryIndex) => {
                                      if (newCategoryIndex !== editingTaskRef.categoryIndex) {
                                        const newTaskIndex = config.workflowConfiguration.taskReference.categories[newCategoryIndex].options.length;
                                        moveTaskToCategory(editingTaskRef.categoryIndex, editingTaskRef.taskIndex, newCategoryIndex);
                                        setEditingTaskRef({ categoryIndex: newCategoryIndex, taskIndex: newTaskIndex });
                                      }
                                    }}
                                  />
                                  <FormHelperText fontSize="xs">
                                    Select which category this task reference belongs to
                                  </FormHelperText>
                                </FormControl>

                                <Divider />

                                <Box>
                                  <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.700">Task Reference Fields</Text>
                                  <Text fontSize="xs" color="gray.600" mb={3}>
                                    Visible fields appear on the Content Engine form. Required fields must be filled before continuing.
                                  </Text>
                                  <VStack spacing={3} align="stretch">
                                    {availableFields.map(fieldKey => {
                                      const taskRef = config.workflowConfiguration.taskReference.categories[editingTaskRef.categoryIndex].options[editingTaskRef.taskIndex];
                                      const isVisible = getTaskRefVisibleFields(taskRef).includes(fieldKey);
                                      const isRequired = getTaskRefRequiredFields(taskRef).includes(fieldKey);

                                      return (
                                        <Box key={fieldKey} p={3} borderWidth="1px" borderColor="gray.200" borderRadius="lg" bg="gray.50">
                                          <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={3}>
                                            {getFieldDisplayLabel(fieldKey)}
                                          </Text>
                                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                            <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                              <FormLabel fontSize="xs" color="gray.600" mb={0}>Visible</FormLabel>
                                              <Switch
                                                isChecked={isVisible}
                                                onChange={(e) => updateVisibleMetadata(editingTaskRef.categoryIndex, editingTaskRef.taskIndex, fieldKey, e.target.checked)}
                                                colorScheme="blue"
                                                size="lg"
                                              />
                                            </FormControl>
                                            <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                              <FormLabel fontSize="xs" color="gray.600" mb={0}>Required</FormLabel>
                                              <Switch
                                                isChecked={isRequired}
                                                onChange={(e) => updateRequiredMetadata(editingTaskRef.categoryIndex, editingTaskRef.taskIndex, fieldKey, e.target.checked)}
                                                colorScheme="green"
                                                size="lg"
                                                isDisabled={!isVisible}
                                              />
                                            </FormControl>
                                          </SimpleGrid>
                                        </Box>
                                      );
                                    })}
                                  </VStack>
                                </Box>

                                <Flex justify="flex-end" spacing={3} mt={4}>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingTaskRef(null);
                                      if (showOnlySection === 'taskRefs') {
                                        returnToOverview();
                                      }
                                    }}
                                    borderRadius="lg"
                                    mr={3}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    colorScheme="blue"
                                    onClick={async () => {
                                      setEditingTaskRef(null);
                                      await saveToDatabase();
                                    }}
                                    isLoading={isSaving}
                                    loadingText="Saving..."
                                    borderRadius="lg"
                                  >
                                    Save Changes
                                  </Button>
                                </Flex>
                               </VStack>
                             </CardBody>
                           </Card>
                         ) : (
                    // Show categories and their tasks
                    <VStack spacing={6} align="stretch">
                      {config?.workflowConfiguration?.taskReference?.categories?.map((category, categoryIndex) => (
                                           <Card 
                          key={categoryIndex}
                          bg="rgba(255, 255, 255, 0.8)"
                          backdropFilter="blur(10px)"
                          borderRadius="xl"
                          shadow="lg"
                          border="1px solid"
                          borderColor="rgba(255, 255, 255, 0.2)"
                          _hover={{
                            transform: "translateY(-4px)",
                            shadow: "xl",
                            bg: "rgba(255, 255, 255, 0.9)"
                          }}
                          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                        >
                          <CardHeader>
                            <Flex justify="space-between" align="center">
                              <HStack spacing={3}>
                                <Flex
                                  w="52px"
                                  h="52px"
                                  borderRadius="full"
                                  align="center"
                                  justify="center"
                                  bg="purple.50"
                                  border="1px solid"
                                  borderColor="purple.200"
                                  color="purple.600"
                                  flexShrink={0}
                                >
                                  {React.createElement(
                                    category.icon && FiIcons[`Fi${category.icon}`] ? FiIcons[`Fi${category.icon}`] : FiIcons.FiFolder,
                                    { size: 24 }
                                  )}
                                </Flex>
                                <VStack align="start" spacing={1}>
                                  <Heading size="md" color="purple.700" fontFamily="Sora, sans-serif">{category.name}</Heading>
                                  <Text fontSize="sm" color="gray.500">{category.description}</Text>
                                </VStack>
                              </HStack>
                              <Badge colorScheme="purple" borderRadius="full">{category.options?.length || 0} tasks</Badge>
                            </Flex>
                          </CardHeader>

                          <CardBody pt={0}>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                              {category.options?.map((taskRef, taskIndex) => (
                                <Card
                                  key={`${categoryIndex}-${taskIndex}`}
                        bg="rgba(255, 255, 255, 0.8)" 
                        backdropFilter="blur(10px)"
                        borderRadius="xl" 
                        shadow="lg" 
                        border="1px solid"
                        borderColor="rgba(255, 255, 255, 0.2)"
                        _hover={{ 
                          transform: "translateY(-8px) scale(1.02)", 
                          shadow: "xl",
                          bg: "rgba(255, 255, 255, 0.9)"
                        }}
                        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                      >
                       <CardHeader pb={3}>
                         <VStack align="start" spacing={3}>
                           <HStack justify="space-between" w="full">
                                                               <Heading size="md" color="blue.700" fontFamily="Sora, sans-serif">{taskRef.label}</Heading>
                                        <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>{taskIndex + 1}</Badge>
                                      </HStack>
                                      <HStack spacing={2} align="center">
                                        <Text fontSize="sm" color="gray.600" fontWeight="medium">Category:</Text>
                                        <Badge colorScheme="purple" size="sm" borderRadius="full">
                                          {category.name}
                                        </Badge>
                                        {category.icon && (
                                          <Text fontSize="sm" color="gray.500">
                                            {category.icon}
                                          </Text>
                                        )}
                           </HStack>
                           <Text color="gray.500" fontFamily="mono" fontSize="sm" bg="gray.50" p={2} borderRadius="md">{taskRef.value}</Text>
                           <HStack spacing={1} flexWrap="wrap">
                             {getTaskRefVisibleFields(taskRef).map(fieldName => (
                               <Badge
                                 key={fieldName}
                                 colorScheme={getTaskRefRequiredFields(taskRef).includes(fieldName) ? 'green' : 'blue'}
                                 size="sm"
                                 borderRadius="full"
                               >
                                 {getFieldDisplayLabel(fieldName)} {getTaskRefRequiredFields(taskRef).includes(fieldName) ? '(Required)' : '(Visible)'}
                               </Badge>
                             ))}
                           </HStack>
                         </VStack>
                       </CardHeader>

                       <CardBody pt={0} pb={4}>
                                    {editingTaskRef && editingTaskRef.categoryIndex === categoryIndex && editingTaskRef.taskIndex === taskIndex ? (
                           <VStack spacing={4} align="stretch">
                             <SimpleGrid columns={2} spacing={4}>
                               <FormControl>
                                 <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Label</FormLabel>
                                 <Input
                                   value={taskRef.label}
                                              onChange={(e) => updateTaskReference(categoryIndex, taskIndex, 'label', e.target.value)}
                                   placeholder="Enter task reference label"
                                   size="sm"
                                   borderRadius="lg"
                                   border="1px solid"
                                   borderColor="gray.300"
                                 />
                               </FormControl>
                               
                               <FormControl>
                                 <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Value</FormLabel>
                                 <Input
                                   value={taskRef.value}
                                              onChange={(e) => updateTaskReference(categoryIndex, taskIndex, 'value', e.target.value)}
                                   placeholder="Enter task reference value"
                                   size="sm"
                                   borderRadius="lg"
                                   border="1px solid"
                                   borderColor="gray.300"
                                 />
                               </FormControl>
                             </SimpleGrid>

                             <FormControl>
                               <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Category</FormLabel>
                               <CategorySelectDropdown
                                 categories={config?.workflowConfiguration?.taskReference?.categories || []}
                                 value={categoryIndex}
                                 onChange={(newCategoryIndex) => {
                                   if (newCategoryIndex !== categoryIndex) {
                                     const newTaskIndex = config.workflowConfiguration.taskReference.categories[newCategoryIndex].options.length;
                                     moveTaskToCategory(categoryIndex, taskIndex, newCategoryIndex);
                                     setEditingTaskRef({ categoryIndex: newCategoryIndex, taskIndex: newTaskIndex });
                                   }
                                 }}
                               />
                               <FormHelperText fontSize="xs">
                                 Select which category this task reference belongs to
                               </FormHelperText>
                             </FormControl>

                             <Divider />

                             <Box>
                               <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.700">Task Reference Fields</Text>
                               <Text fontSize="xs" color="gray.600" mb={3}>
                                 Visible fields appear on the Content Engine form. Required fields must be filled before continuing.
                               </Text>
                               <VStack spacing={3} align="stretch">
                                 {availableFields.map(fieldKey => {
                                   const isVisible = getTaskRefVisibleFields(taskRef).includes(fieldKey);
                                   const isRequired = getTaskRefRequiredFields(taskRef).includes(fieldKey);

                                   return (
                                     <Box key={fieldKey} p={3} borderWidth="1px" borderColor="gray.200" borderRadius="lg" bg="gray.50">
                                       <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={3}>
                                         {getFieldDisplayLabel(fieldKey)}
                                       </Text>
                                       <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                         <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                           <FormLabel fontSize="xs" color="gray.600" mb={0}>Visible</FormLabel>
                                           <Switch
                                             isChecked={isVisible}
                                             onChange={(e) => updateVisibleMetadata(categoryIndex, taskIndex, fieldKey, e.target.checked)}
                                             colorScheme="blue"
                                             size="lg"
                                           />
                                         </FormControl>
                                         <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                           <FormLabel fontSize="xs" color="gray.600" mb={0}>Required</FormLabel>
                                           <Switch
                                             isChecked={isRequired}
                                             onChange={(e) => updateRequiredMetadata(categoryIndex, taskIndex, fieldKey, e.target.checked)}
                                             colorScheme="green"
                                             size="lg"
                                             isDisabled={!isVisible}
                                           />
                                         </FormControl>
                                       </SimpleGrid>
                                     </Box>
                                   );
                                 })}
                               </VStack>
                             </Box>

                             <HStack spacing={3} mt={4}>
                               <Button
                                 size="sm"
                                 colorScheme="blue"
                                 onClick={async () => {
                                   setEditingTaskRef(null);
                                   await saveToDatabase();
                                 }}
                                 isLoading={isSaving}
                                 loadingText="Saving..."
                                 flex={1}
                                 borderRadius="lg"
                               >
                                 Save Changes
                               </Button>
                               <Button
                                 size="sm"
                                 variant="outline"
                                 onClick={() => {
                                   setEditingTaskRef(null);
                                   if (showOnlySection === 'taskRefs') {
                                     returnToOverview();
                                   }
                                 }}
                                 flex={1}
                                 borderRadius="lg"
                               >
                                 Cancel
                               </Button>
                             </HStack>
                           </VStack>
                         ) : (
                           <HStack spacing={3} w="full">
                             <Button
                               size="sm"
                               colorScheme="blue"
                               variant="outline"
                                          onClick={() => setEditingTaskRef({ categoryIndex, taskIndex })}
                               flex={1}
                               borderRadius="lg"
                             >
                               Edit
                             </Button>
                             <IconButton
                               icon={<DeleteIcon color="white" />}
                               bg="red.500"
                               color="white"
                               size="sm"
                               variant="solid"
                                          onClick={() => removeTaskReference(categoryIndex, taskIndex)}
                               borderRadius="full"
                               _hover={{ bg: "red.600" }}
                               _active={{ bg: "red.700" }}
                             />
                           </HStack>
                                                                                                                                         )}
                           </CardBody>
                         </Card>
                       ))}
                             </SimpleGrid>
                          </CardBody>
                        </Card>
                      ))}
                    </VStack>
                           )}
                         </>
                       ))}
                     </VStack>
                   )}
             {(activeTab === 'fields' || showOnlySection === 'fields') && (
               <VStack spacing={6} align="stretch">
                 {showOnlySection === 'fields' && (
                   <Flex justify="space-between" align="center">
                     <Heading size="lg" color="gray.800" fontFamily="Sora, sans-serif">Form Fields</Heading>
                     <Button
                       leftIcon={<ViewIcon />}
                       colorScheme="green"
                       variant="outline"
                       size="sm"
                       onClick={returnToOverview}
                       borderRadius="lg"
                     >
                       Back to Overview
                     </Button>
                   </Flex>
                 )}
                 {addingNewItem.type === 'field' ? (
                   // New Field Form
                   <VStack spacing={6} align="stretch">
                     <Flex justify="space-between" align="center">
                       <Heading size="lg" color="gray.800">Add New Field</Heading>
                       <Button
                         leftIcon={<CloseIcon />}
                         variant="ghost"
                         onClick={cancelNewField}
                         borderRadius="lg"
                       >
                         Cancel
                       </Button>
                     </Flex>
                     
                     <Card 
                       bg="rgba(255, 255, 255, 0.8)" 
                       backdropFilter="blur(10px)"
                       borderRadius="xl" 
                       shadow="lg" 
                       border="1px solid"
                       borderColor="rgba(255, 255, 255, 0.2)"
                       _hover={{ 
                         transform: "translateY(-4px)", 
                         shadow: "xl",
                         bg: "rgba(255, 255, 255, 0.9)"
                       }}
                       transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                     >
                       <CardHeader>
                         <Heading size="md" color="green.700">New Field Details</Heading>
                       </CardHeader>
                       <CardBody>
                         <VStack spacing={4} align="stretch">
                           <SimpleGrid columns={2} spacing={4}>
                             <FormControl>
                               <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Field Key</FormLabel>
                               <Input 
                                 value={addingNewItem.data?.fieldKey || ""} 
                                 onChange={(e) => setAddingNewItem(prev => ({
                                   ...prev,
                                   data: { ...prev.data, fieldKey: e.target.value }
                                 }))}
                                 placeholder="Enter field key (e.g., selfEfficacyQuiz)"
                                 size="sm" 
                                 borderRadius="lg" 
                                 border="1px solid"
                                 borderColor="gray.300"
                               />
                               <FormHelperText fontSize="xs">This is the unique identifier for the field (e.g., selfEfficacyQuiz)</FormHelperText>
                             </FormControl>
                             
                             <FormControl>
                               <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Label</FormLabel>
                               <Input
                                 value={addingNewItem.data?.field?.label || ""}
                                 onChange={(e) => updateNewField('label', e.target.value)}
                                 placeholder="Enter field label"
                                 size="sm"
                                 borderRadius="lg"
                                 border="1px solid"
                                 borderColor="gray.300"
                               />
                             </FormControl>
                           </SimpleGrid>

                           <Divider />

                           <SimpleGrid columns={3} spacing={4}>
                             <FormControl>
                               <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Type</FormLabel>
                               <Select
                                 value={addingNewItem.data?.field?.type || "single"}
                                 onChange={(e) => updateNewField('type', e.target.value)}
                                 size="sm"
                                 borderRadius="lg"
                                 border="1px solid"
                                 borderColor="gray.300"
                               >
                                 <option value="single">Single Selection</option>
                                 <option value="multiple">Multiple Selection</option>
                                 <option value="input">Input Field</option>
                                  <option value="slider">Slider</option>
                               </Select>
                             </FormControl>
                             
                             <FormControl>
                               <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Required</FormLabel>
                                                               <Switch
                                  isChecked={addingNewItem.data?.field?.required || false}
                                  onChange={(e) => updateNewField('required', e.target.checked)}
                                  colorScheme="red"
                                  size="lg"
                                />
                               <FormHelperText fontSize="xs">
                                 {addingNewItem.data?.field?.required ? 'Field is required' : 'Field is optional'}
                               </FormHelperText>
                             </FormControl>
                             
                             <FormControl>
                               <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Default Visible</FormLabel>
                                                               <Switch
                                  isChecked={addingNewItem.data?.field?.defaultVisible || false}
                                  onChange={(e) => updateNewField('defaultVisible', e.target.checked)}
                                  colorScheme="green"
                                  size="lg"
                                />
                               <FormHelperText fontSize="xs">
                                 {addingNewItem.data?.field?.defaultVisible ? 'Shown by default' : 'Hidden by default'}
                               </FormHelperText>
                             </FormControl>
                           </SimpleGrid>

                          {/* Slider-specific configuration (multi-slider) */}
                          {addingNewItem.data?.field?.type === 'slider' && (
                            <Box mt={4} p={4} bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.200">
                              <Flex justify="space-between" align="center" mb={3}>
                                <Text fontSize="sm" fontWeight="medium" color="blue.700">Slider Configuration</Text>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  colorScheme="blue"
                                  onClick={() => {
                                    const newSliders = [...(addingNewItem.data?.field?.e8Sliders || []),
                                      { key: 'newSlider', label: 'New Slider', min: 0, max: 100, defaultValue: 0 }
                                    ];
                                    updateNewField('e8Sliders', newSliders);
                                  }}
                                  borderRadius="md"
                                >
                                  + Add Slider
                                </Button>
                              </Flex>

                              <VStack spacing={4} align="stretch">
                                {(addingNewItem.data?.field?.e8Sliders || []).map((slider, index) => (
                                  <Card key={index} size="sm" bg="white" border="1px solid" borderColor="blue.200">
                                    <CardBody p={3}>
                                      <Flex justify="space-between" align="center" mb={2}>
                                        <Text fontSize="xs" color="gray.600" fontWeight="semibold">Slider #{index + 1}</Text>
                                        <Button
                                          size="xs"
                                          colorScheme="red"
                                          variant="ghost"
                                          onClick={() => {
                                            const newSliders = [...(addingNewItem.data?.field?.e8Sliders || [])];
                                            newSliders.splice(index, 1);
                                            updateNewField('e8Sliders', newSliders);
                                          }}
                                        >
                                          Delete
                                        </Button>
                                      </Flex>
                                      <SimpleGrid columns={5} spacing={3}>
                                        <FormControl>
                                          <FormLabel fontSize="xs" color="gray.600">Key</FormLabel>
                                          <Input
                                            value={slider.key}
                                            onChange={(e) => {
                                              const newSliders = [...(addingNewItem.data?.field?.e8Sliders || [])];
                                              newSliders[index] = { ...slider, key: e.target.value };
                                              updateNewField('e8Sliders', newSliders);
                                            }}
                                            size="sm"
                                            borderRadius="md"
                                            fontFamily="mono"
                                          />
                                        </FormControl>

                                        <FormControl>
                                          <FormLabel fontSize="xs" color="gray.600">Label</FormLabel>
                                          <Input
                                            value={slider.label}
                                            onChange={(e) => {
                                              const newSliders = [...(addingNewItem.data?.field?.e8Sliders || [])];
                                              newSliders[index] = { ...slider, label: e.target.value };
                                              updateNewField('e8Sliders', newSliders);
                                            }}
                                            size="sm"
                                            borderRadius="md"
                                          />
                                        </FormControl>

                                        <FormControl>
                                          <FormLabel fontSize="xs" color="gray.600">Min</FormLabel>
                                          <Input
                                            type="number"
                                            value={slider.min}
                                            onChange={(e) => {
                                              const newSliders = [...(addingNewItem.data?.field?.e8Sliders || [])];
                                              newSliders[index] = { ...slider, min: parseInt(e.target.value) || 0 };
                                              updateNewField('e8Sliders', newSliders);
                                            }}
                                            size="sm"
                                            borderRadius="md"
                                          />
                                        </FormControl>

                                        <FormControl>
                                          <FormLabel fontSize="xs" color="gray.600">Max</FormLabel>
                                          <Input
                                            type="number"
                                            value={slider.max}
                                            onChange={(e) => {
                                              const newSliders = [...(addingNewItem.data?.field?.e8Sliders || [])];
                                              newSliders[index] = { ...slider, max: parseInt(e.target.value) || 100 };
                                              updateNewField('e8Sliders', newSliders);
                                            }}
                                            size="sm"
                                            borderRadius="md"
                                          />
                                        </FormControl>

                                        <FormControl>
                                          <FormLabel fontSize="xs" color="gray.600">Default</FormLabel>
                                          <Input
                                            type="number"
                                            value={slider.defaultValue}
                                            onChange={(e) => {
                                              const newSliders = [...(addingNewItem.data?.field?.e8Sliders || [])];
                                              newSliders[index] = { ...slider, defaultValue: parseInt(e.target.value) || 0 };
                                              updateNewField('e8Sliders', newSliders);
                                            }}
                                            size="sm"
                                            borderRadius="md"
                                          />
                                        </FormControl>
                                      </SimpleGrid>
                                    </CardBody>
                                  </Card>
                                ))}
                              </VStack>
                            </Box>
                          )}

                           {/* E8 Scoring configuration */}
                           {addingNewItem.data?.field?.type === 'e8scoring' && (
                             <Box mt={4} p={4} bg="green.50" borderRadius="lg" border="1px solid" borderColor="green.200">
                               <Text fontSize="sm" fontWeight="medium" color="green.700" mb={3}>E8 Scoring Configuration</Text>
                               <Text fontSize="xs" color="green.600" mb={4}>
                                 Configure multiple E8 Score sliders. Each slider will be used for mock data configuration.
                               </Text>
                               
                               <VStack spacing={4} align="stretch">
                                 {(addingNewItem.data?.field?.e8Sliders || [
                                   { key: 'nicotineExposure', label: 'Nicotine Exposure', min: 0, max: 100, defaultValue: 75 },
                                   { key: 'physicalActivity', label: 'Physical Activity', min: 0, max: 100, defaultValue: 0 },
                                   { key: 'sleep', label: 'Sleep Quality', min: 0, max: 100, defaultValue: 0 },
                                   { key: 'eating', label: 'Eating Habits', min: 0, max: 100, defaultValue: 50 }
                                 ]).map((slider, index) => (
                                   <Card key={index} size="sm" bg="white" border="1px solid" borderColor="green.200">
                                     <CardBody p={3}>
                                       <SimpleGrid columns={4} spacing={3}>
                                         <FormControl>
                                           <FormLabel fontSize="xs" color="gray.600">Key</FormLabel>
                                           <Input
                                             value={slider.key}
                                             onChange={(e) => {
                                               const newSliders = [...(addingNewItem.data?.field?.e8Sliders || [])];
                                               newSliders[index] = { ...slider, key: e.target.value };
                                               updateNewField('e8Sliders', newSliders);
                                             }}
                                             size="sm"
                                             borderRadius="md"
                                             fontFamily="mono"
                                           />
                                         </FormControl>
                                         
                                         <FormControl>
                                           <FormLabel fontSize="xs" color="gray.600">Label</FormLabel>
                                           <Input
                                             value={slider.label}
                                             onChange={(e) => {
                                               const newSliders = [...(addingNewItem.data?.field?.e8Sliders || [])];
                                               newSliders[index] = { ...slider, label: e.target.value };
                                               updateNewField('e8Sliders', newSliders);
                                             }}
                                             size="sm"
                                             borderRadius="md"
                                           />
                                         </FormControl>
                                         
                                         <FormControl>
                                           <FormLabel fontSize="xs" color="gray.600">Min</FormLabel>
                                           <Input
                                             type="number"
                                             value={slider.min}
                                             onChange={(e) => {
                                               const newSliders = [...(addingNewItem.data?.field?.e8Sliders || [])];
                                               newSliders[index] = { ...slider, min: parseInt(e.target.value) || 0 };
                                               updateNewField('e8Sliders', newSliders);
                                             }}
                                             size="sm"
                                             borderRadius="md"
                                           />
                                         </FormControl>
                                         
                                         <FormControl>
                                           <FormLabel fontSize="xs" color="gray.600">Max</FormLabel>
                                           <Input
                                             type="number"
                                             value={slider.max}
                                             onChange={(e) => {
                                               const newSliders = [...(addingNewItem.data?.field?.e8Sliders || [])];
                                               newSliders[index] = { ...slider, max: parseInt(e.target.value) || 100 };
                                               updateNewField('e8Sliders', newSliders);
                                             }}
                                             size="sm"
                                             borderRadius="md"
                                           />
                                         </FormControl>
                                       </SimpleGrid>
                                       
                                       <HStack justify="space-between" mt={2}>
                                        <Text fontSize="xs" color="gray.500">Default: {slider.defaultValue}</Text>
                                         <Input
                                           type="number"
                                           value={slider.defaultValue}
                                           onChange={(e) => {
                                             const newSliders = [...(addingNewItem.data?.field?.e8Sliders || [])];
                                              newSliders[index] = { ...slider, defaultValue: parseInt(e.target.value) || 0 };
                                             updateNewField('e8Sliders', newSliders);
                                           }}
                                           size="xs"
                                           w="60px"
                                           borderRadius="md"
                                         />
                                       </HStack>
                                     </CardBody>
                                   </Card>
                                 ))}
                                 
                                 <Button
                                   size="sm"
                                   variant="outline"
                                   colorScheme="green"
                                   onClick={() => {
                                     const newSliders = [...(addingNewItem.data?.field?.e8Sliders || []), 
                                      { key: 'newSlider', label: 'New Slider', min: 0, max: 100, defaultValue: 0 }
                                     ];
                                     updateNewField('e8Sliders', newSliders);
                                   }}
                                   borderRadius="md"
                                 >
                                   + Add Slider
                                 </Button>
                               </VStack>
                               
                               <Text fontSize="xs" color="green.600" mt={3}>
                                 These sliders will be used for E8 Score mock data configuration in task forms.
                               </Text>
                             </Box>
                           )}

                                                       <Flex justify="flex-end" spacing={3} mt={4}>
                              <Button
                                size="md"
                                variant="outline"
                                onClick={cancelNewField}
                                borderRadius="lg"
                                mr={3}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="md"
                                colorScheme="green"
                                onClick={saveNewField}
                                isLoading={isSaving}
                                loadingText="Saving..."
                                borderRadius="lg"
                              >
                                Save New Field
                              </Button>
                            </Flex>
                         </VStack>
                       </CardBody>
                     </Card>
                   </VStack>
                 ) : (
                   // Existing Fields List
                   <>
                     {showOnlySection !== 'fields' && (
                       <Flex justify="space-between" align="center">
                         <Heading size="lg" color="gray.800">Form Fields</Heading>
                         <Button
                           leftIcon={<AddIcon />}
                           colorScheme="green"
                           size="md"
                           onClick={addField}
                           borderRadius="lg"
                         >
                           Add Field
                         </Button>
                       </Flex>
                     )}
                     
                                                                                       {editingField !== null ? (
                           // Show only the editing form - Full width
                           <Card 
                             key={editingField} 
                             bg="rgba(255, 255, 255, 0.8)" 
                             backdropFilter="blur(10px)"
                             borderRadius="xl" 
                             shadow="lg" 
                             border="1px solid"
                             borderColor="rgba(255, 255, 255, 0.2)"
                             _hover={{ 
                               transform: "translateY(-8px) scale(1.02)", 
                               shadow: "xl",
                               bg: "rgba(255, 255, 255, 0.9)"
                             }}
                             transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                             w="full"
                           >
                            <CardHeader pb={3}>
                              <VStack align="start" spacing={3}>
                                <HStack justify="space-between" w="full">
                                  <Heading size="md" color="green.700">{config.workflowConfiguration[editingField].label}</Heading>
                                                                     <Badge colorScheme="green" borderRadius="full" px={3} py={1}>{config.workflowConfiguration[editingField].type}</Badge>
                                </HStack>
                                <Text color="gray.500" fontFamily="mono" fontSize="sm" bg="gray.50" p={2} borderRadius="md">{editingField}</Text>
                                <HStack spacing={1} flexWrap="wrap">
                                                                     {config.workflowConfiguration[editingField].required && <Badge colorScheme="red" size="sm" borderRadius="full" px={2} py={1}>Required</Badge>}
                                   {config.workflowConfiguration[editingField].defaultVisible && <Badge colorScheme="green" size="sm" borderRadius="full" px={2} py={1}>Default Visible</Badge>}
                                   {config.workflowConfiguration[editingField].options && <Badge colorScheme="purple" size="sm" borderRadius="full" px={2} py={1}>{config.workflowConfiguration[editingField].options.length} options</Badge>}
                                </HStack>
                              </VStack>
                            </CardHeader>

                            <CardBody pt={0} pb={4}>
                              <VStack spacing={4} align="stretch">
                                <SimpleGrid columns={2} spacing={4}>
                                  <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Field Key</FormLabel>
                                    <Input value={editingField} isReadOnly bg="transparent" size="sm" borderRadius="lg" />
                                    <FormHelperText fontSize="xs">This is the unique identifier for the field</FormHelperText>
                                  </FormControl>
                                  
                                  <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Label</FormLabel>
                                    <Input
                                      value={config.workflowConfiguration[editingField].label}
                                      onChange={(e) => updateField(editingField, 'label', e.target.value)}
                                      placeholder="Enter field label"
                                      size="sm"
                                      borderRadius="lg"
                                      border="1px solid"
                                      borderColor="gray.300"
                                    />
                                  </FormControl>
                                </SimpleGrid>

                                <Divider />

                                <SimpleGrid columns={3} spacing={4}>
                                  <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Type</FormLabel>
                                    <Select
                                      value={config.workflowConfiguration[editingField].type}
                                      onChange={(e) => updateField(editingField, 'type', e.target.value)}
                                      size="sm"
                                      borderRadius="lg"
                                      border="1px solid"
                                      borderColor="gray.300"
                                    >
                                      <option value="single">Single Selection</option>
                                      <option value="multiple">Multiple Selection</option>
                                      <option value="input">Input Field</option>
                                      <option value="slider">Slider</option>
                                    </Select>
                                  </FormControl>
                                  
                                  <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Required</FormLabel>
                                                                       <Switch
                                     isChecked={config.workflowConfiguration[editingField].required}
                                     onChange={(e) => updateField(editingField, 'required', e.target.checked)}
                                     colorScheme="red"
                                     size="lg"
                                   />
                                    <FormHelperText fontSize="xs">
                                      {config.workflowConfiguration[editingField].required ? 'Field is required' : 'Field is optional'}
                                    </FormHelperText>
                                  </FormControl>
                                  
                                  <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Default Visible</FormLabel>
                                                                       <Switch
                                     isChecked={config.workflowConfiguration[editingField].defaultVisible}
                                     onChange={(e) => updateField(editingField, 'defaultVisible', e.target.checked)}
                                     colorScheme="green"
                                     size="lg"
                                   />
                                    <FormHelperText fontSize="xs">
                                      {config.workflowConfiguration[editingField].defaultVisible ? 'Shown by default' : 'Hidden by default'}
                                    </FormHelperText>
                                  </FormControl>
                                </SimpleGrid>

                                {/* Slider-specific configuration */}
                                {config.workflowConfiguration[editingField].type === 'slider' && (
                                  <>
                                    <Divider />
                                    <Box>
                                      <Flex justify="space-between" align="center" mb={3}>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.700">Slider Configuration</Text>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          colorScheme="blue"
                                          onClick={() => {
                                            const newSliders = [...(config.workflowConfiguration[editingField].e8Sliders || []),
                                              { key: 'newSlider', label: 'New Slider', min: 0, max: 100, defaultValue: 0 }
                                            ];
                                            updateField(editingField, 'e8Sliders', newSliders);
                                          }}
                                          borderRadius="md"
                                        >
                                          + Add Slider
                                        </Button>
                                      </Flex>

                                      <VStack spacing={4} align="stretch">
                                        {(config.workflowConfiguration[editingField].e8Sliders || []).map((slider, index) => (
                                          <Card key={index} size="sm" bg="white" border="1px solid" borderColor="blue.200">
                                            <CardBody p={3}>
                                              <Flex justify="space-between" align="center" mb={2}>
                                                <Text fontSize="xs" color="gray.600" fontWeight="semibold">Slider #{index + 1}</Text>
                                                <Button
                                                  size="xs"
                                                  colorScheme="red"
                                                  variant="ghost"
                                                  onClick={() => {
                                                    const newSliders = [...(config.workflowConfiguration[editingField].e8Sliders || [])];
                                                    newSliders.splice(index, 1);
                                                    updateField(editingField, 'e8Sliders', newSliders);
                                                  }}
                                                >
                                                  Delete
                                                </Button>
                                              </Flex>
                                              <SimpleGrid columns={5} spacing={3}>
                                                <FormControl>
                                                  <FormLabel fontSize="xs" color="gray.600">Key</FormLabel>
                                                  <Input
                                                    value={slider.key}
                                                    onChange={(e) => {
                                                      const newSliders = [...(config.workflowConfiguration[editingField].e8Sliders || [])];
                                                      newSliders[index] = { ...slider, key: e.target.value };
                                                      updateField(editingField, 'e8Sliders', newSliders);
                                                    }}
                                                    size="sm"
                                                    borderRadius="md"
                                                    fontFamily="mono"
                                                  />
                                                </FormControl>

                                                <FormControl>
                                                  <FormLabel fontSize="xs" color="gray.600">Label</FormLabel>
                                                  <Input
                                                    value={slider.label}
                                                    onChange={(e) => {
                                                      const newSliders = [...(config.workflowConfiguration[editingField].e8Sliders || [])];
                                                      newSliders[index] = { ...slider, label: e.target.value };
                                                      updateField(editingField, 'e8Sliders', newSliders);
                                                    }}
                                                    size="sm"
                                                    borderRadius="md"
                                                  />
                                                </FormControl>

                                                <FormControl>
                                                  <FormLabel fontSize="xs" color="gray.600">Min</FormLabel>
                                                  <Input
                                                    type="number"
                                                    value={slider.min}
                                                    onChange={(e) => {
                                                      const newSliders = [...(config.workflowConfiguration[editingField].e8Sliders || [])];
                                                      newSliders[index] = { ...slider, min: parseInt(e.target.value) || 0 };
                                                      updateField(editingField, 'e8Sliders', newSliders);
                                                    }}
                                                    size="sm"
                                                    borderRadius="md"
                                                  />
                                                </FormControl>

                                                <FormControl>
                                                  <FormLabel fontSize="xs" color="gray.600">Max</FormLabel>
                                                  <Input
                                                    type="number"
                                                    value={slider.max}
                                                    onChange={(e) => {
                                                      const newSliders = [...(config.workflowConfiguration[editingField].e8Sliders || [])];
                                                      newSliders[index] = { ...slider, max: parseInt(e.target.value) || 100 };
                                                      updateField(editingField, 'e8Sliders', newSliders);
                                                    }}
                                                    size="sm"
                                                    borderRadius="md"
                                                  />
                                                </FormControl>

                                                <FormControl>
                                                  <FormLabel fontSize="xs" color="gray.600">Default</FormLabel>
                                                  <Input
                                                    type="number"
                                                    value={slider.defaultValue}
                                                    onChange={(e) => {
                                                      const newSliders = [...(config.workflowConfiguration[editingField].e8Sliders || [])];
                                                      newSliders[index] = { ...slider, defaultValue: parseInt(e.target.value) || 0 };
                                                      updateField(editingField, 'e8Sliders', newSliders);
                                                    }}
                                                    size="sm"
                                                    borderRadius="md"
                                                  />
                                                </FormControl>
                                              </SimpleGrid>
                                            </CardBody>
                                          </Card>
                                        ))}
                                      </VStack>
                                    </Box>
                                  </>
                                )}

                                {config.workflowConfiguration[editingField].type !== 'input' && config.workflowConfiguration[editingField].type !== 'slider' && (
                                  <>
                                    <Divider />
                                    <Box>
                                      <Flex justify="space-between" align="center" mb={3}>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.700">Options</Text>
                                        <Button
                                          size="sm"
                                          colorScheme="green"
                                          variant="outline"
                                          onClick={() => addFieldOption(editingField)}
                                          leftIcon={<AddIcon />}
                                          borderRadius="lg"
                                        >
                                          Add Option
                                        </Button>
                                      </Flex>
                                      
                                      <VStack spacing={3} align="stretch">
                                        {config.workflowConfiguration[editingField].options?.map((option, optionIndex) => (
                                          <Card key={optionIndex} variant="outline" size="sm" borderRadius="lg">
                                            <CardBody py={3}>
                                              <SimpleGrid columns={2} spacing={3}>
                                                <FormControl size="sm">
                                                  <FormLabel fontSize="xs" color="gray.600">Value</FormLabel>
                                                  <Input
                                                    value={option.value}
                                                    onChange={(e) => {
                                                      const newOptions = [...config.workflowConfiguration[editingField].options];
                                                      newOptions[optionIndex] = { ...option, value: e.target.value };
                                                      updateField(editingField, 'options', newOptions);
                                                    }}
                                                    size="sm"
                                                    borderRadius="lg"
                                                    border="1px solid"
                                                    borderColor="gray.300"
                                                  />
                                                </FormControl>
                                                <FormControl size="sm">
                                                  <FormLabel fontSize="xs" color="gray.600">Label</FormLabel>
                                                  <Input
                                                    value={option.label}
                                                    onChange={(e) => {
                                                      const newOptions = [...config.workflowConfiguration[editingField].options];
                                                      newOptions[optionIndex] = { ...option, label: e.target.value };
                                                      updateField(editingField, 'options', newOptions);
                                                    }}
                                                    size="sm"
                                                    borderRadius="lg"
                                                    border="1px solid"
                                                    borderColor="gray.300"
                                                  />
                                                </FormControl>
                                              </SimpleGrid>
                                              <HStack justify="space-between" mt={3}>
                                                <Checkbox
                                                  isChecked={option.default}
                                                  onChange={(e) => {
                                                    const newOptions = [...config.workflowConfiguration[editingField].options];
                                                    newOptions[optionIndex] = { ...option, default: e.target.checked };
                                                    updateField(editingField, 'options', newOptions);
                                                  }}
                                                  size="sm"
                                                >
                                                  Default
                                                </Checkbox>
                                                <IconButton
                                                  icon={<DeleteIcon color="white" />}
                                                  bg="red.500"
                                                  color="white"
                                                  size="sm"
                                                  variant="solid"
                                                  onClick={() => removeFieldOption(editingField, optionIndex)}
                                                  borderRadius="full"
                                                  _hover={{ bg: "red.600" }}
                                                  _active={{ bg: "red.700" }}
                                                />
                                              </HStack>
                                            </CardBody>
                                          </Card>
                                        ))}
                                      </VStack>
                                    </Box>
                                  </>
                                )}

                                <Flex justify="flex-end" spacing={3} mt={4}>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingField(null);
                                      if (showOnlySection === 'fields') {
                                        returnToOverview();
                                      }
                                    }}
                                    borderRadius="lg"
                                    mr={3}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    colorScheme="green"
                                    onClick={async () => {
                                      setEditingField(null);
                                      await saveToDatabase();
                                    }}
                                    isLoading={isSaving}
                                    loadingText="Saving..."
                                    borderRadius="lg"
                                  >
                                    Save Changes
                                  </Button>
                                </Flex>
                              </VStack>
                            </CardBody>
                          </Card>
                        ) : (
                          // Show all fields in grid
                          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                            {availableFields.map(fieldKey => {
                              const field = config.workflowConfiguration[fieldKey];
                              return (
                                <Card
                                  key={fieldKey}
                                  bg="rgba(255, 255, 255, 0.8)"
                                  backdropFilter="blur(10px)"
                                  borderRadius="xl"
                                  shadow="lg"
                                  border="1px solid"
                                  borderColor="rgba(255, 255, 255, 0.2)"
                                  _hover={{
                                    transform: "translateY(-8px) scale(1.02)",
                                    shadow: "xl",
                                    bg: "rgba(255, 255, 255, 0.9)"
                                  }}
                                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                >
                         <CardHeader pb={3}>
                           <VStack align="start" spacing={3}>
                             <HStack justify="space-between" w="full">
                               <Heading size="md" color="green.700">{field.label}</Heading>
                               <Badge colorScheme="green" borderRadius="full">{field.type}</Badge>
                             </HStack>
                             <Text color="gray.500" fontFamily="mono" fontSize="sm" bg="gray.50" p={2} borderRadius="md">{fieldKey}</Text>
                             <HStack spacing={1} flexWrap="wrap">
                               {field.required && <Badge colorScheme="red" size="sm" borderRadius="full">Required</Badge>}
                               {field.defaultVisible && <Badge colorScheme="green" size="sm" borderRadius="full">Default Visible</Badge>}
                               {field.options && <Badge colorScheme="purple" size="sm" borderRadius="full">{field.options.length} options</Badge>}
                             </HStack>
                           </VStack>
                         </CardHeader>

                         <CardBody pt={0} pb={4}>
                           {editingField === fieldKey ? (
                             <VStack spacing={4} align="stretch">
                               <SimpleGrid columns={2} spacing={4}>
                                 <FormControl>
                                   <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Field Key</FormLabel>
                                   <Input value={fieldKey} isReadOnly bg="transparent" size="sm" borderRadius="lg" />
                                   <FormHelperText fontSize="xs">This is the unique identifier for the field</FormHelperText>
                                 </FormControl>
                                 
                                 <FormControl>
                                   <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Label</FormLabel>
                                   <Input
                                     value={field.label}
                                     onChange={(e) => updateField(fieldKey, 'label', e.target.value)}
                                     placeholder="Enter field label"
                                     size="sm"
                                     borderRadius="lg"
                                     border="1px solid"
                                     borderColor="gray.300"
                                   />
                                 </FormControl>
                               </SimpleGrid>

                               <Divider />

                               <SimpleGrid columns={3} spacing={4}>
                                 <FormControl>
                                   <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Type</FormLabel>
                                   <Select
                                     value={field.type}
                                     onChange={(e) => updateField(fieldKey, 'type', e.target.value)}
                                     size="sm"
                                     borderRadius="lg"
                                     border="1px solid"
                                     borderColor="gray.300"
                                   >
                                     <option value="single">Single Selection</option>
                                     <option value="multiple">Multiple Selection</option>
                                     <option value="input">Input Field</option>
                                   </Select>
                                 </FormControl>
                                 
                                 <FormControl>
                                   <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Required</FormLabel>
                                                                       <Switch
                                      isChecked={field.required}
                                      onChange={(e) => updateField(fieldKey, 'required', e.target.checked)}
                                      colorScheme="red"
                                      size="lg"
                                    />
                                   <FormHelperText fontSize="xs">
                                     {field.required ? 'Field is required' : 'Field is optional'}
                                   </FormHelperText>
                                 </FormControl>
                                 
                                 <FormControl>
                                   <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Default Visible</FormLabel>
                                                                       <Switch
                                      isChecked={field.defaultVisible}
                                      onChange={(e) => updateField(fieldKey, 'defaultVisible', e.target.checked)}
                                      colorScheme="green"
                                      size="lg"
                                    />
                                   <FormHelperText fontSize="xs">
                                     {field.defaultVisible ? 'Shown by default' : 'Hidden by default'}
                                   </FormHelperText>
                                 </FormControl>
                               </SimpleGrid>

                               {field.type !== 'input' && (
                                 <>
                                   <Divider />
                                   <Box>
                                     <Flex justify="space-between" align="center" mb={3}>
                                       <Text fontSize="sm" fontWeight="medium" color="gray.700">Options</Text>
                                       <Button
                                         size="sm"
                                         colorScheme="green"
                                         variant="outline"
                                         onClick={() => addFieldOption(fieldKey)}
                                         leftIcon={<AddIcon />}
                                         borderRadius="lg"
                                       >
                                         Add Option
                                       </Button>
                                     </Flex>
                                     
                                     <VStack spacing={3} align="stretch">
                                       {field.options?.map((option, optionIndex) => (
                                         <Card key={optionIndex} variant="outline" size="sm" borderRadius="lg">
                                           <CardBody py={3}>
                                             <SimpleGrid columns={2} spacing={3}>
                                               <FormControl size="sm">
                                                 <FormLabel fontSize="xs" color="gray.600">Value</FormLabel>
                                                 <Input
                                                   value={option.value}
                                                   onChange={(e) => {
                                                     const newOptions = [...field.options];
                                                     newOptions[optionIndex] = { ...option, value: e.target.value };
                                                     updateField(fieldKey, 'options', newOptions);
                                                   }}
                                                   size="sm"
                                                   borderRadius="lg"
                                                   border="1px solid"
                                                   borderColor="gray.300"
                                                 />
                                               </FormControl>
                                               <FormControl size="sm">
                                                 <FormLabel fontSize="xs" color="gray.600">Label</FormLabel>
                                                 <Input
                                                   value={option.label}
                                                   onChange={(e) => {
                                                     const newOptions = [...field.options];
                                                     newOptions[optionIndex] = { ...option, label: e.target.value };
                                                     updateField(fieldKey, 'options', newOptions);
                                                   }}
                                                   size="sm"
                                                   borderRadius="lg"
                                                   border="1px solid"
                                                   borderColor="gray.300"
                                                 />
                                               </FormControl>
                                             </SimpleGrid>
                                             <HStack justify="space-between" mt={3}>
                                               <Checkbox
                                                 isChecked={option.default}
                                                 onChange={(e) => {
                                                   const newOptions = [...field.options];
                                                   newOptions[optionIndex] = { ...option, default: e.target.checked };
                                                   updateField(fieldKey, 'options', newOptions);
                                                 }}
                                                 size="sm"
                                               >
                                                 Default
                                               </Checkbox>
                                               <IconButton
                                                 icon={<DeleteIcon color="white" />}
                                                 bg="red.500"
                                                 color="white"
                                                 size="sm"
                                                 variant="solid"
                                                 onClick={() => removeFieldOption(fieldKey, optionIndex)}
                                                 borderRadius="full"
                                                 _hover={{ bg: "red.600" }}
                                                 _active={{ bg: "red.700" }}
                                               />
                                             </HStack>
                                           </CardBody>
                                         </Card>
                                       ))}
                                     </VStack>
                                   </Box>
                                 </>
                               )}

                               <Flex justify="flex-end" spacing={3} mt={4}>
                                 <Button
                                   size="sm"
                                   variant="outline"
                                   onClick={() => {
                                     setEditingField(null);
                                     if (showOnlySection === 'fields') {
                                       returnToOverview();
                                     }
                                   }}
                                   borderRadius="lg"
                                   mr={3}
                                 >
                                   Cancel
                                 </Button>
                                 <Button
                                   size="sm"
                                   colorScheme="green"
                                   onClick={async () => {
                                     setEditingField(null);
                                     await saveToDatabase();
                                   }}
                                   isLoading={isSaving}
                                   loadingText="Saving..."
                                   borderRadius="lg"
                                 >
                                   Save Changes
                                 </Button>
                               </Flex>
                             </VStack>
                           ) : (
                             <HStack spacing={3} w="full">
                               <Button
                                 size="sm"
                                 colorScheme="green"
                                 variant="outline"
                                 onClick={() => setEditingField(fieldKey)}
                                 flex={1}
                                 borderRadius="lg"
                               >
                                 Edit
                               </Button>
                               <IconButton
                                 icon={<DeleteIcon color="white" />}
                                 bg="red.500"
                                 color="white"
                                 size="sm"
                                 variant="solid"
                                 onClick={() => removeField(fieldKey)}
                                 borderRadius="full"
                                 _hover={{ bg: "red.600" }}
                                 _active={{ bg: "red.700" }}
                               />
                             </HStack>
                                                       )}
                          </CardBody>
                        </Card>
                      );
                    })}
                            </SimpleGrid>
                          )}
                 </>
               )}
               </VStack>
             )}
           </Box>

          {/* Rule Engine Section */}
          {(activeTab === 'ruleEngine' || showOnlySection === 'ruleEngine') && (
            <Box w="full" maxW="100%" overflowX="hidden">
              {showOnlySection === 'ruleEngine' && (
                <Button
                  leftIcon={<ViewIcon />}
                  colorScheme="blue"
                  variant="outline"
                  size="sm"
                  onClick={returnToOverview}
                  borderRadius="lg"
                  mb={4}
                >
                  Back to Overview
                </Button>
              )}
              
              <Card mb={6} bg="gradient.50" border="1px solid" borderColor="blue.200" borderRadius="xl" boxShadow="lg" w="full" maxW="100%">
                <CardHeader pb={3} bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" borderTopRadius="xl">
                  <HStack>
                    <Box bg="white" p={2} borderRadius="lg" opacity={0.9}>
                      <SettingsIcon color="blue.600" boxSize={6} />
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xl" fontWeight="bold" color="white" fontFamily="Sora, sans-serif">
                        Rule Engine
                      </Text>
                      <Text fontSize="sm" color="white" opacity={0.9} fontFamily="Roboto, sans-serif">
                        Manage JSON rule engine rules with advanced condition building
                      </Text>
                    </VStack>
                  </HStack>
                </CardHeader>

                <CardBody pt={6}>
                  {/* Action Buttons */}
                  <Flex justify="flex-end" gap={3} mb={6}>
                    <Button
                      leftIcon={<AddIcon />}
                      colorScheme="blue"
                      variant="solid"
                      size="md"
                      onClick={() => {
                        setIsCreatingRule(true);
                        setEditingRule(null);
                        setTestingRule(null);
                        setTestFacts({});
                        setTestResult(null);
                        setExpandedRuleId(null);
                      }}
                      borderRadius="lg"
                      isDisabled={isCreatingRule || testingRule}
                    >
                      Create Rule
                    </Button>
                    <Button
                      leftIcon={<CheckIcon />}
                      colorScheme="purple"
                      variant="outline"
                      size="md"
                      onClick={() => {
                        setTestingRule({ name: 'Test Rule', conditions: { all: [] }, event: { type: 'getOutcome', params: {} } });
                        setTestFacts({});
                        setTestResult(null);
                        setIsCreatingRule(false);
                        setEditingRule(null);
                        setExpandedRuleId(null);
                      }}
                      borderRadius="lg"
                      isDisabled={isCreatingRule || testingRule}
                    >
                      Test Rule
                    </Button>
                  </Flex>

                  {/* Statistics - Hide when creating or testing */}
                  {!isCreatingRule && !testingRule && (
                  <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4} mb={6}>
                    <GridItem>
                      <Card bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" color="white" borderRadius="xl" shadow="md">
                        <CardBody>
                          <Stat>
                            <StatLabel fontSize="sm" opacity={0.9}>Total Rules</StatLabel>
                            <StatNumber fontSize="2xl" fontWeight="bold">{rules.length}</StatNumber>
                            <StatHelpText opacity={0.8}>Active rules</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                    </GridItem>
                    <GridItem>
                      <Card bg="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" color="white" borderRadius="xl" shadow="md">
                        <CardBody>
                          <Stat>
                            <StatLabel fontSize="sm" opacity={0.9}>Filtered</StatLabel>
                            <StatNumber fontSize="2xl" fontWeight="bold">
                              {getFilteredAndSortedRules().total}
                            </StatNumber>
                            <StatHelpText opacity={0.8}>Matching results</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                    </GridItem>
                    <GridItem>
                      <Card bg="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" color="white" borderRadius="xl" shadow="md">
                        <CardBody>
                          <Stat>
                            <StatLabel fontSize="sm" opacity={0.9}>Page</StatLabel>
                            <StatNumber fontSize="2xl" fontWeight="bold">
                              {currentPage}/{getFilteredAndSortedRules().totalPages || 1}
                            </StatNumber>
                            <StatHelpText opacity={0.8}>Current page</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                    </GridItem>
                    <GridItem>
                      <Card bg="linear-gradient(135deg, #fa709a 0%, #fee140 100%)" color="white" borderRadius="xl" shadow="md">
                        <CardBody>
                          <Stat>
                            <StatLabel fontSize="sm" opacity={0.9}>Available Facts</StatLabel>
                            <StatNumber fontSize="2xl" fontWeight="bold">{availableFacts.length}</StatNumber>
                            <StatHelpText opacity={0.8}>Fact types</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                    </GridItem>
                  </Grid>
                  )}

                  {/* Rules Data Grid - Hide when creating or testing */}
                  {!isCreatingRule && !testingRule && (
                  <Card mb={6} borderRadius="xl" shadow="sm" w="full" maxW="100%" overflowX="auto">
                    <CardHeader bg="gray.50" borderTopRadius="xl" pb={3}>
                      <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                        <Text fontSize="lg" fontWeight="bold" color="gray.800" fontFamily="Sora, sans-serif">
                          Rules Table
                        </Text>
                        <HStack spacing={2} flex={1} maxW="500px" minW="200px">
                          <InputGroup>
                            <InputLeftElement pointerEvents="none">
                              <SearchIcon color="gray.400" />
                            </InputLeftElement>
                            <Input
                              placeholder="Search rules..."
                              value={ruleFilter}
                              onChange={(e) => {
                                setRuleFilter(e.target.value);
                                setCurrentPage(1);
                              }}
                              bg="white"
                              border="1px solid"
                              borderColor="gray.300"
                              borderRadius="lg"
                              _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                            />
                          </InputGroup>
                        </HStack>
                      </Flex>
                    </CardHeader>
                    
                    <CardBody pt={0} w="full" overflowX="auto">
                      {rulesLoading ? (
                        <Flex justify="center" align="center" py={10}>
                          <Spinner size="xl" color="blue.500" />
                        </Flex>
                      ) : (
                        <Box w="full" overflowX="auto">
                          <TableContainer w="full" overflowX="auto">
                            <Table variant="simple" size="md" minW="600px">
                              <Thead bg="gray.50">
                                <Tr>
                                  <Th 
                                    cursor="pointer" 
                                    onClick={() => handleSort('name')}
                                    _hover={{ bg: 'gray.100' }}
                                    userSelect="none"
                                  >
                                    <HStack spacing={2}>
                                      <Text>Name</Text>
                                      {ruleSortBy === 'name' && (
                                        <TriangleDownIcon 
                                          transform={ruleSortOrder === 'desc' ? 'rotate(180deg)' : 'none'}
                                          transition="transform 0.2s"
                                        />
                                      )}
                                    </HStack>
                                  </Th>
                                  <Th 
                                    cursor="pointer" 
                                    onClick={() => handleSort('taskRefId')}
                                    _hover={{ bg: 'gray.100' }}
                                    userSelect="none"
                                  >
                                    <HStack spacing={2}>
                                      <Text>Task Reference ID</Text>
                                      {ruleSortBy === 'taskRefId' && (
                                        <TriangleDownIcon 
                                          transform={ruleSortOrder === 'desc' ? 'rotate(180deg)' : 'none'}
                                          transition="transform 0.2s"
                                        />
                                      )}
                                    </HStack>
                                  </Th>
                                  <Th textAlign="center">Actions</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {getFilteredAndSortedRules().filtered.length === 0 ? (
                                  <Tr>
                                    <Td colSpan={3} textAlign="center" py={10}>
                                      <VStack spacing={2}>
                                        <Text color="gray.500">No rules found</Text>
                                        {ruleFilter && (
                                          <Button size="sm" variant="ghost" onClick={() => setRuleFilter('')}>
                                            Clear filter
                                          </Button>
                                        )}
                                      </VStack>
                                    </Td>
                                  </Tr>
                                ) : (
                                  getFilteredAndSortedRules().filtered.map((rule, index) => {
                                    const isExpanded = expandedRuleId === rule.id;
                                    // Extract facts from conditions
                                    const extractFacts = (conditions) => {
                                      const facts = new Set();
                                      if (!conditions) return Array.from(facts);
                                      
                                      const traverse = (obj) => {
                                        if (Array.isArray(obj)) {
                                          obj.forEach(item => traverse(item));
                                        } else if (typeof obj === 'object' && obj !== null) {
                                          if (obj.fact) {
                                            facts.add(obj.fact);
                                          }
                                          Object.values(obj).forEach(value => traverse(value));
                                        }
                                      };
                                      
                                      traverse(conditions);
                                      return Array.from(facts);
                                    };
                                    
                                    const ruleFacts = extractFacts(rule.conditions);
                                    const taskRefId = extractTaskRefId(rule.conditions);
                                    
                                    return (
                                      <React.Fragment key={rule.id || index}>
                                        <Tr _hover={{ bg: 'blue.50' }} transition="background 0.2s">
                                          <Td>
                                            <VStack align="start" spacing={1}>
                                              <Text fontWeight="semibold" color="gray.800">
                                                {rule.name || 'Unnamed Rule'}
                                              </Text>
                                              <Badge colorScheme="blue" size="sm" borderRadius="full">
                                                ID: {rule.id}
                                              </Badge>
                                            </VStack>
                                          </Td>
                                          <Td>
                                            <Badge 
                                              colorScheme={taskRefId !== 'N/A' ? "green" : "gray"} 
                                              size="md" 
                                              borderRadius="full"
                                              px={4}
                                              py={2}
                                              fontSize="sm"
                                              fontWeight="medium"
                                            >
                                              {taskRefId}
                                            </Badge>
                                          </Td>
                                          <Td>
                                            <HStack spacing={2} justify="center">
                                              <IconButton
                                                icon={<EditIcon />}
                                                size="sm"
                                                colorScheme="blue"
                                                variant="outline"
                                                onClick={() => {
                                                  setEditingRule(rule);
                                                  setIsCreatingRule(false);
                                                }}
                                                borderRadius="lg"
                                                title="Edit Rule"
                                              />
                                              <IconButton
                                                icon={isExpanded ? <ChevronDownIcon /> : <ViewIcon />}
                                                size="sm"
                                                colorScheme={isExpanded ? "orange" : "green"}
                                                variant={isExpanded ? "solid" : "outline"}
                                                onClick={() => {
                                                  setExpandedRuleId(isExpanded ? null : rule.id);
                                                }}
                                                borderRadius="lg"
                                                title={isExpanded ? "Collapse Details" : "View Details"}
                                              />
                                              <IconButton
                                                icon={<CheckIcon />}
                                                size="sm"
                                                colorScheme="purple"
                                                variant="outline"
                                                onClick={() => {
                                                  setTestingRule(rule);
                                                  setTestFacts({});
                                                  setTestResult(null);
                                                }}
                                                borderRadius="lg"
                                                title="Test Rule"
                                              />
                                              <IconButton
                                                icon={<DeleteIcon />}
                                                size="sm"
                                                colorScheme="red"
                                                variant="outline"
                                                onClick={() => {
                                                  setDeleteRuleConfirm({ show: true, rule: rule });
                                                }}
                                                borderRadius="lg"
                                                title="Delete Rule"
                                              />
                                            </HStack>
                                          </Td>
                                        </Tr>
                                        
                                        {/* Expanded Row Details */}
                                        {isExpanded && (
                                          <Tr bg="gray.50">
                                            <Td colSpan={3} p={6}>
                                              <Card bg="white" borderRadius="xl" shadow="md">
                                                <CardHeader bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" borderTopRadius="xl">
                                                  <Flex justify="space-between" align="center">
                                                    <HStack>
                                                      <Text fontSize="lg" fontWeight="bold" color="white" fontFamily="Sora, sans-serif">
                                                        Rule Details: {rule.name}
                                                      </Text>
                                                      <Badge colorScheme="whiteAlpha" fontSize="sm" px={2} py={1}>
                                                        ID: {rule.id}
                                                      </Badge>
                                                    </HStack>
                                                    <Button
                                                      size="sm"
                                                      variant="ghost"
                                                      colorScheme="whiteAlpha"
                                                      onClick={() => setExpandedRuleId(null)}
                                                      _hover={{ bg: 'whiteAlpha.200' }}
                                                    >
                                                      <CloseIcon />
                                                    </Button>
                                                  </Flex>
                                                </CardHeader>
                                                
                                                <CardBody pt={6}>
                                                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                                                    {/* Conditions Section */}
                                                    <Box>
                                                      <Text fontSize="md" fontWeight="bold" color="gray.700" mb={4} fontFamily="Sora, sans-serif">
                                                        Conditions
                                                      </Text>
                                                      <Card bg="gray.50" borderRadius="lg" p={4} border="1px solid" borderColor="gray.200">
                                                        <Box
                                                          p={3}
                                                          bg="gray.800"
                                                          borderRadius="md"
                                                          fontFamily="mono"
                                                          fontSize="xs"
                                                          color="green.300"
                                                          maxH="300px"
                                                          overflowY="auto"
                                                        >
                                                          <Text whiteSpace="pre-wrap">{JSON.stringify(rule.conditions, null, 2)}</Text>
                                                        </Box>
                                                      </Card>
                                                      
                                                      {/* Facts Used */}
                                                      {ruleFacts.length > 0 && (
                                                        <Box mt={4}>
                                                          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                                                            Facts Used in This Rule:
                                                          </Text>
                                                          <Flex wrap="wrap" gap={2}>
                                                            {ruleFacts.map((fact, idx) => (
                                                              <Badge key={idx} colorScheme="blue" borderRadius="full" px={3} py={1}>
                                                                {fact}
                                                              </Badge>
                                                            ))}
                                                          </Flex>
                                                        </Box>
                                                      )}
                                                    </Box>
                                                    
                                                    {/* Event/Outcome Section */}
                                                    <Box>
                                                      <Text fontSize="md" fontWeight="bold" color="gray.700" mb={4} fontFamily="Sora, sans-serif">
                                                        Event / Outcome
                                                      </Text>
                                                      <Card bg="green.50" borderRadius="lg" p={4} border="1px solid" borderColor="green.200">
                                                        <VStack spacing={3} align="stretch">
                                                          <Box>
                                                            <Text fontSize="xs" fontWeight="semibold" color="green.700" mb={1}>
                                                              Event Type:
                                                            </Text>
                                                            <Badge colorScheme="green" fontSize="sm" px={3} py={1} borderRadius="full">
                                                              {rule.event?.type || 'N/A'}
                                                            </Badge>
                                                          </Box>
                                                          
                                                          <Box>
                                                            <Text fontSize="xs" fontWeight="semibold" color="green.700" mb={2}>
                                                              Parameters:
                                                            </Text>
                                                            <Box
                                                              p={3}
                                                              bg="gray.800"
                                                              borderRadius="md"
                                                              fontFamily="mono"
                                                              fontSize="xs"
                                                              color="green.300"
                                                              maxH="200px"
                                                              overflowY="auto"
                                                            >
                                                              <Text whiteSpace="pre-wrap">
                                                                {JSON.stringify(rule.event?.params || {}, null, 2)}
                                                              </Text>
                                                            </Box>
                                                          </Box>
                                                          
                                                          {/* Full Event JSON */}
                                                          <Box>
                                                            <Text fontSize="xs" fontWeight="semibold" color="green.700" mb={2}>
                                                              Full Event JSON:
                                                            </Text>
                                                            <Box
                                                              p={3}
                                                              bg="gray.800"
                                                              borderRadius="md"
                                                              fontFamily="mono"
                                                              fontSize="xs"
                                                              color="green.300"
                                                              maxH="200px"
                                                              overflowY="auto"
                                                            >
                                                              <Text whiteSpace="pre-wrap">
                                                                {JSON.stringify(rule.event, null, 2)}
                                                              </Text>
                                                            </Box>
                                                          </Box>
                                                        </VStack>
                                                      </Card>
                                                      
                                                      {/* Quick Actions */}
                                                      <HStack spacing={3} mt={4}>
                                                        <Button
                                                          leftIcon={<EditIcon />}
                                                          colorScheme="blue"
                                                          size="sm"
                                                          variant="outline"
                                                          onClick={() => {
                                                            setEditingRule(rule);
                                                            setIsCreatingRule(false);
                                                            setExpandedRuleId(null);
                                                          }}
                                                          borderRadius="lg"
                                                        >
                                                          Edit Rule
                                                        </Button>
                                                        <Button
                                                          leftIcon={<CheckIcon />}
                                                          colorScheme="purple"
                                                          size="sm"
                                                          variant="outline"
                                                          onClick={() => {
                                                            setTestingRule(rule);
                                                            setTestFacts({});
                                                            setTestResult(null);
                                                            setExpandedRuleId(null);
                                                          }}
                                                          borderRadius="lg"
                                                        >
                                                          Test Rule
                                                        </Button>
                                                      </HStack>
                                                    </Box>
                                                  </SimpleGrid>
                                                </CardBody>
                                              </Card>
                                            </Td>
                                          </Tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })
                                )}
                              </Tbody>
                            </Table>
                          </TableContainer>

                          {/* Pagination */}
                          {getFilteredAndSortedRules().totalPages > 1 && (
                            <Flex justify="space-between" align="center" mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
                              <Text fontSize="sm" color="gray.600">
                                Showing {((currentPage - 1) * rulesPerPage) + 1} to{' '}
                                {Math.min(currentPage * rulesPerPage, getFilteredAndSortedRules().total)} of{' '}
                                {getFilteredAndSortedRules().total} rules
                              </Text>
                              <HStack spacing={2}>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                  isDisabled={currentPage === 1}
                                  borderRadius="lg"
                                >
                                  Previous
                                </Button>
                                <Text fontSize="sm" color="gray.600" px={2}>
                                  Page {currentPage} of {getFilteredAndSortedRules().totalPages}
                                </Text>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setCurrentPage(prev => Math.min(getFilteredAndSortedRules().totalPages, prev + 1))}
                                  isDisabled={currentPage === getFilteredAndSortedRules().totalPages}
                                  borderRadius="lg"
                                >
                                  Next
                                </Button>
                              </HStack>
                            </Flex>
                          )}
                        </Box>
                      )}
                    </CardBody>
                  </Card>
                  )}

                  {/* Rule Builder / Editor */}
                  {(isCreatingRule || editingRule) && (
                    <RuleBuilder
                      rule={editingRule}
                      availableFacts={availableFacts}
                      onSave={async (ruleData) => {
                        if (editingRule) {
                          await updateRule(editingRule.id, ruleData);
                        } else {
                          await createRule(ruleData);
                        }
                      }}
                      onCancel={() => {
                        setIsCreatingRule(false);
                        setEditingRule(null);
                      }}
                      onTest={testRule}
                      isSaving={isSaving}
                    />
                  )}

                  {/* Rule Testing - Show standalone when not from a specific rule */}
                  {testingRule && (
                    <RuleTester
                      rule={testingRule}
                      availableFacts={availableFacts}
                      testFacts={testFacts}
                      setTestFacts={setTestFacts}
                      testResult={testResult}
                      isTesting={isTesting}
                      onTest={async () => {
                        await testRule(
                          typeof testingRule.conditions === 'string' 
                            ? JSON.parse(testingRule.conditions) 
                            : testingRule.conditions,
                          testFacts
                        );
                      }}
                      onClose={() => {
                        setTestingRule(null);
                        setTestFacts({});
                        setTestResult(null);
                      }}
                      isStandalone={true}
                    />
                  )}
                </CardBody>
              </Card>
            </Box>
          )}
       </Flex>
      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirm.show} onClose={() => setDeleteConfirm({ show: false, type: '', index: null, fieldKey: null })} size="md">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="error" borderRadius="lg">
              <AlertIcon />
              <Box>
                <AlertTitle>Delete Confirmation</AlertTitle>
                <AlertDescription>
                  Are you sure you want to delete this {deleteConfirm.type === 'taskRef' ? 'task reference' : 'field'}? This action cannot be undone.
                </AlertDescription>
              </Box>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setDeleteConfirm({ show: false, type: '', index: null, fieldKey: null })}>
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              onClick={confirmDelete}
              isLoading={isSaving}
              loadingText="Deleting..."
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Rule Confirmation Modal */}
      <Modal isOpen={deleteRuleConfirm.show} onClose={() => setDeleteRuleConfirm({ show: false, rule: null })} size="md">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>Confirm Delete Rule</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="error" borderRadius="lg">
              <AlertIcon />
              <Box>
                <AlertTitle>Delete Confirmation</AlertTitle>
                <AlertDescription>
                  Are you sure you want to delete the rule <strong>"{deleteRuleConfirm.rule?.name}"</strong>? This action cannot be undone.
                  {deleteRuleConfirm.rule?.id && (
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Rule ID: {deleteRuleConfirm.rule.id}
                    </Text>
                  )}
                </AlertDescription>
              </Box>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setDeleteRuleConfirm({ show: false, rule: null })}>
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              onClick={async () => {
                if (deleteRuleConfirm.rule?.id) {
                  await deleteRule(deleteRuleConfirm.rule.id);
                }
                setDeleteRuleConfirm({ show: false, rule: null });
              }}
              isLoading={isSaving}
              loadingText="Deleting..."
            >
              Delete Rule
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default WorkflowConfigEditor;